import { useState, useEffect, useCallback, createContext, useContext, useRef } from "react";
import { api } from "./api";

// ─── AUTH CONTEXT ───────────────────────────────────────────
const AuthCtx = createContext({ loggedIn: false, admin: null, session: null, login: () => {}, logout: () => {}, refreshSession: () => {} });
function useAuth() { return useContext(AuthCtx); }

// ─── ROUTE CONTEXT ──────────────────────────────────────────
const RouteCtx = createContext({ page: "/", go: () => {}, linkProps: () => ({}) });
function useRoute() { return useContext(RouteCtx); }

// ─── SEO ────────────────────────────────────────────────────
const SITE_URL = "https://steingoldpsychology.com";
const SITE_NAME = "Dr. Eugenia Steingold, PhD: NYC Adult & Child Psychologist";
const DEFAULT_DESC =
  "Dr. Eugenia Steingold, PhD | Clinical Psychologist in Manhattan With 20+ years' Experience | Psychotherapy, Trauma EMDR, Child & Adolescent Neuropsychological Testing, Family Counseling, In-person and Virtual sessions.";
const OG_IMAGE = `${SITE_URL}/media/eugeniasteingold.png`;

// Upsert a <head> tag in place so we never duplicate the static defaults.
function upsertHeadTag(tag, attrs, content) {
  const keyAttr = attrs.name ? "name" : attrs.property ? "property" : attrs.rel ? "rel" : null;
  const selector = keyAttr ? `${tag}[${keyAttr}="${attrs[keyAttr]}"]` : tag;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tag);
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  if (content != null) el.setAttribute("content", content);
  return el;
}

// Imperative, dedupe-safe SEO. React 19 can hoist <title>/<meta>, but mutating
// the existing static tags avoids duplicate meta when combined with the
// no-JS / social-scraper defaults baked into index.html.
function useSeo({ title, description = DEFAULT_DESC, path = "/", image = OG_IMAGE, type = "website", noindex = false } = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME}`;
    const url = SITE_URL + (path === "/" ? "/" : path.replace(/#.*$/, ""));
    document.title = fullTitle;
    upsertHeadTag("meta", { name: "description" }, description);
    upsertHeadTag("link", { rel: "canonical", href: url });
    upsertHeadTag("meta", { name: "robots" }, noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    upsertHeadTag("meta", { property: "og:title" }, fullTitle);
    upsertHeadTag("meta", { property: "og:description" }, description);
    upsertHeadTag("meta", { property: "og:url" }, url);
    upsertHeadTag("meta", { property: "og:image" }, image);
    upsertHeadTag("meta", { property: "og:type" }, type);
    upsertHeadTag("meta", { property: "og:site_name" }, SITE_NAME);
    upsertHeadTag("meta", { name: "twitter:card" }, "summary_large_image");
    upsertHeadTag("meta", { name: "twitter:title" }, fullTitle);
    upsertHeadTag("meta", { name: "twitter:description" }, description);
    upsertHeadTag("meta", { name: "twitter:image" }, image);
  }, [title, description, path, image, type, noindex]);
}

// Injects (and cleans up) a JSON-LD structured-data block. Data blocks are not
// executable, so they are allowed under a strict script-src CSP.
function useJsonLd(id, data) {
  useEffect(() => {
    if (!data) return undefined;
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => { el.remove(); };
  }, [id, data]);
}

// ─── CONSTANTS ──────────────────────────────────────────────
const PHONE = "(347) 779-2473";
const EMAIL = "Dr.Eugenia.Steingold@gmail.com";
const ADDRESS = "28 W 44th St, Suite 714, New York, NY 10036";
const MAPS_URL = "https://maps.app.goo.gl/4xhvtE9obCkKY8Qz8";
const OLIVE = "#8EB027";
const BABY_BLUE = "#cce5f7";

const DR_PHOTO = "/media/eugeniasteingold.webp";

function getBlogImageUrlFromAsset(assetId) {
  if (!assetId) return "";
  return `/api/public/media/${assetId}`;
}

function getBlogImageUrl(blog) {
  if (!blog) return "";
  if (blog.imgSource === "url" && blog.imgUrl) return blog.imgUrl;
  if (blog.imgSource === "upload" && blog.imgAssetId) return getBlogImageUrlFromAsset(blog.imgAssetId);
  if (blog.img && typeof blog.img === "string" && !blog.img.startsWith("data:")) return blog.img;
  return "";
}

function textToHtml(text = "") {
  const safe = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return safe
    .split(/\n{2,}/)
    .map(p => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
    .join("");
}

const DEFAULT_BLOGS = [
  { id:"1", slug:"adhd-hyposensitive", title:"ADHD? Hyposensitive? What to do.", desc:"ADHD? Hyposensitive? What To Do.", date:"July 9, 2017", img:"https://drsensory.com/wp-content/uploads/2025/08/DrSensory-Kids-Care-scaled.jpg", content:"I cannot over-estimate the importance of daily chores. They are great for self-esteem, organization, sense of contributing to the family. The point is not to overwhelm your child with work, but to make sure s/he is doing something meaningful, something that leads to your appreciation." },
  { id:"2", slug:"child-bully", title:"When your child is the bully…", desc:"When your child is the bully…", date:"February 22, 2017", img:"https://static.scientificamerican.com/dam/m/3a409ad6063fe07a/original/children_bullying.jpg", content:"It’s hard to imaging anything more upsetting, frustrating and confusing than learning that your own child is the bully. There is also embarrassment as you learn gradually the whole story (because you learned it first from your baby and s/he didn’t say the whole truth, or maybe didn’t say the truth at all). Well, now you know it. Your baby teased another child. Maybe even hit another child. Took toys from another child. Your baby made another child cry and feel miserable. Now you know the truth. What to do next?" },
  { id:"3", slug:"psychodynamic-therapy", title:"The Power of Psychodynamic Therapy", desc:"Uncover the roots of your patterns and behaviors to create lasting change.", date:"January 18, 1975", img:"/media/blog-therapy.jpg", content:"Psychodynamic therapy invites us to look beneath the surface. Rather than focusing solely on symptoms, we explore the deeper currents — early experiences, relationships, and unconscious patterns — that shape how we move through the world...\n\nThis approach is not about dwelling in the past. It's about understanding how the past lives in the present, so we can finally move forward." }
];

const TESTIMONIALS_FULL = [
  { text: "In the time that I have known her, Dr. Steingold has shown herself to be a supremely caring, compassionate, and competent professional. She shows great concern for her patients and is very knowledgeable and pleasant to deal with. I highly recommend Dr. Steingold as a great therapist who truly cares about her patients.", author: "Alla B." },
  { text: "I have sent many patients to Dr. Steingold and have found her to be very accurate in her assessment and diagnosis. I have seen tremendous improvement in patients of all ages, and they have come back to me extremely grateful for the referral. She has literally changed peoples' lives! Dr. Steingold’s exceptional training along with her calming personality make her an absolutely wonderful therapist!", author: "Vita O." },
  { text: "Dr. Steingold was highly recommended by my friends and I had signed my dad for her sessions. He was very satisfied with her services. She was described as caring, kind and understanding. My dad told his friends about her too.", author: "Leah P." },
  { text: "Dr. Steingold is absolutely the best!!! There is no problem she hasn't helped me deal with in the most awesome way. She is brilliant, compassionate, focused, solution oriented and professional. All those I have referred to Dr. Steingold are thrilled.", author: "Chanie R." },
  { text: "I am an attorney, and from time to time need to refer my clients to a psychotherapist. The clients I referred to Dr. Steingold thanked me for the referral and were very happy that they were introduced to Dr. Steingold.", author: "Daniel H." },
  { text: "Dr. Steingold is a very talented and caring treatment provider. She helped me with a number of issues and thinks outside the box to suggest many possible solutions to your problems. Dr. Steingold listens very keenly and is a valuable partner in treatment. She is also knowledgeable about medications. I wouldn't hesitate to recommend her.", author: "Anonymous" },
  { text: "I would highly recommend Dr. Steingold. She is great to talk to and is very supportive. She really makes you feel like someone is there to listen and is behind you to help you work things through and make the best decision for yourself. I would highly recommend her.", author: "Anonymous" },
  { text: "Dr Steingold was very accommodating in the schedule, attentive to my child and thorough in her recommendations and follow up. She sincerely cared about our situation and my questions. Very good experience!", author: "Anonymous" }
];

const TESTIMONIALS_STUBS = [
  { text: "When something is wrong with you, I guess the first and the most important thing is finding someone who understands you and what's wrong - without it you cannot even start getting better. But it is even more crucial when something is wrong with your child, and no one seems to understand. Dr. Steingold saw my child three times for a full evaluation. The kid, first reluctant to go or to cooperate, was asking to see the doctor again after the very first visit. Dr. Steingold was able to make my child comfortable, curious and attentive through the first half of our first visit. And while in the beginning my kid was hesitant and mistaking, once felt confident, started showing the results way above the age norm. Not only did Dr. Steingold promptly produce a report with diagnosis and detailed recommendations, but also went through a very detailed plan of the treatment, refraining from medications right away. And now the child and us feel a great relief, know exactly what to do, and are motivated to win over and leave behind what very recently seemed like an insurmountable monster on the way. Would return and highly recommend.", author: "Parent of a child" },
  { text: "Our young son was in crisis. His stress was manifesting in aggressive behaviors, and he was having a hard time controlling his emotions. We were at a loss. Dr. Steingold worked with our son and with us to give him a toolbox for navigating his feelings. She gave us a framework to better understand his needs and how to help him. We are so grateful for her patient and insightful guidance.", author: "Parent of a young boy" },
  { text: "Twice I have used professional services of Dr. Eugenia Steingold for my son. Both times she was incredibly thorough, insightful, and genuinely invested in his progress. Her evaluations were comprehensive, and her recommendations were practical and impactful. I always felt heard and supported as a parent. I recommend Dr. Steingold to anyone who wants the best for their child.", author: "Parent of a young boy" },
  { text: "I tried 3 other therapists after working with Dr. Steingold and kept coming back. Nobody else compares. She has a rare combination of intellect and warmth that makes you feel truly seen. She challenges you when you need it and supports you when you're struggling. She's not just a therapist — she's a lifeline.", author: "Former Patient" },
  { text: "Dear Eugenia. Since we ended our therapy sessions, I have continued to grow in ways I didn't think were possible. The tools you gave me have become part of who I am. I handle stress differently, I communicate better with my family, and I finally feel like I'm living my life instead of just surviving it. Thank you for everything.", author: "H.S., 30 years old" },
  { text: "I came to Eugenia after seeing a few other doctors who just didn't get it. From the first meeting I knew this was different. She truly listens, and her insights changed my life. I finally understand why I kept repeating the same patterns, and I have real strategies to break free. I can't recommend her enough.", author: "H.V., 33 years old" },
  { text: "Dr. Eugenia, thank you for your lofty ambition and the intellectual acumen. We are grateful for your professionalism and approach to individuals with special needs. Seeking your help and assistance was simply a pleasure. Highly recommended.", author: "Schandel M." }
];

const SERVICES_LIST = [
  "Anger Management", "Asperger's Syndrome", "Autism", "Behavioral Issues",
  "Bipolar Disorder", "Body Image", "Child Depression", "Developmental Disorders",
  "Mood Disorders", "School Issues", "Stress", "Trauma and PTSD", "Women's Issues"
];

const FAQ_DATA = [
  { q: "How do I make an appointment?", a: `Fill out the contact form on our Request Appointment page, or call us directly at <a href="tel:${PHONE.replace(/\D/g, '')}" style="color: var(--olive); text-decoration: none;">${PHONE}</a>. We look forward to hearing from you.` },
  { q: "Is my confidentiality guaranteed?", a: "Yes, your confidentiality is protected under HIPAA law. Beyond that, I am fully committed to providing a confidential and emotionally safe setting for every client." },
  { q: "Can I schedule a virtual session with Dr. Steingold?", a: `Yes, Zoom sessions can be selected when booking online or by calling us at <a href="tel:${PHONE.replace(/\D/g, '')}" style="color: var(--olive); text-decoration: none;">${PHONE}</a>. Virtual sessions are available on Tuesdays and by arrangement on other days.` },
  { q: "What insurance plans do you accept?", a: "I am an in-network provider with Aetna. For all other insurance plans, we are happy to submit a claim for your reimbursement on your behalf." },
  { q: "What ages do you work with?", a: "I provide therapy for children and adults. Neuropsychological evaluations are available for ages 2 through 18." },
  { q: "What languages do you offer therapy in?", a: "I am fully bilingual and offer therapy in both English and Russian." }
];

// Service boxes mapped to services page section IDs
const HOME_SERVICES = [
  { icon: "brain", title: "Anxiety & Depression", desc: "Solution-based therapy to re-frame your thinking.", sectionId: "anxiety-depression" },
  { icon: "heart", title: "Neuropsychological Testing", desc: "Comprehensive assessments for children and adolescents.", sectionId: "specialization" },
  { icon: "child", title: "Child Psychology", desc: "Specialized care for developmental and behavioral issues.", sectionId: "child-psych" },
  { icon: "users", title: "Family Counseling", desc: "Support for healthier communication and stronger family relationships.", sectionId: "family" },
  { icon: "target", title: "Trauma & EMDR", desc: "Evidence-based trauma treatment to process and release painful experiences.", sectionId: "trauma-ptsd" },
  { icon: "scale", title: "Health & Executive Coaching", desc: "Strategic coaching for personal growth, health goals, and professional clarity.", sectionId: "exec-coaching" },
];

// ─── STYLES ─────────────────────────────────────────────────
const css = `
:root {
  --olive: #8EB027;
  --olive-dark: #6d8a1b;
  --olive-light: #a8cc3a;
  --baby-blue: #cce5f7;
  --baby-blue-light: #e8f3fc;
  --cream: #fafaf6;
  --warm-gray: #f5f3f0;
  --text: #1a1a1a;
  --text-light: #555;
  --serif: 'Cormorant Garamond', Georgia, serif;
  --serif-display: 'Playfair Display', 'Cormorant Garamond', Georgia, serif;
  --sans: 'Mulish', -apple-system, sans-serif;
}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { scroll-behavior: smooth; }
body {
  font-family: var(--sans); color: #000; line-height: 1.7;
  -webkit-font-smoothing: antialiased; background: #fff;
}
.serif { font-family: var(--serif); }

/* ─── NAV ─── */
.nav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 100;
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(0,0,0,0.06);
  transition: box-shadow 0.3s;
}
.nav.scrolled { box-shadow: 0 2px 20px rgba(0,0,0,0.06); }
.nav-inner {
  max-width: 1200px; margin: 0 auto;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 24px; height: 72px;
}
.nav-brand {
  font-family: var(--serif); font-size: x-large; font-weight: 600;
  color: #000; cursor: pointer; letter-spacing: -0.01em;
  text-decoration: none; line-height: 1.15; white-space: nowrap;
}
.nav-brand span { color: var(--olive); }
.nav-links { display: flex; align-items: center; gap: 32px; }
.nav-link {
  font-size: 0.88rem; font-weight: 500; color: #000;
  cursor: pointer; position: relative; letter-spacing: 0.01em;
  transition: color 0.2s; background: none; border: none;
  font-family: var(--sans); text-decoration: none; white-space: nowrap;
}
.nav-link:hover { color: var(--olive); }
.nav-link::after {
  content: ''; position: absolute; bottom: -4px; left: 0;
  width: 0; height: 1.5px; background: var(--olive);
  transition: width 0.3s;
}
.nav-link:hover::after, .nav-link.active::after { width: 100%; }
.nav-cta {
  background: var(--olive); color: #fff !important;
  padding: 10px 22px; border-radius: 28px; font-size: 0.85rem;
  font-weight: 600; cursor: pointer; border: none;
  font-family: var(--sans); letter-spacing: 0.01em;
  transition: all 0.3s; text-decoration: none; white-space: nowrap;
}
.nav-cta:hover { background: var(--olive-dark); transform: translateY(-1px); box-shadow: 0 4px 14px rgba(142,176,39,0.35); }
.nav-cta::after { display: none !important; }
.nav-cta.hidden { visibility: hidden; pointer-events: none; opacity: 0; transition: opacity 0.3s ease, visibility 0.3s ease; }

.hamburger {
  display: none; background: none; border: none; cursor: pointer;
  width: 28px; height: 20px; position: relative; z-index: 200;
}
.hamburger span {
  display: block; width: 100%; height: 2px; background: #000;
  position: absolute; left: 0; transition: all 0.3s;
}
.hamburger span:nth-child(1) { top: 0; }
.hamburger span:nth-child(2) { top: 9px; }
.hamburger span:nth-child(3) { top: 18px; }
.hamburger.open span:nth-child(1) { top: 9px; transform: rotate(45deg); }
.hamburger.open span:nth-child(2) { opacity: 0; }
.hamburger.open span:nth-child(3) { top: 9px; transform: rotate(-45deg); }

.mobile-menu {
  display: none; position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(255,255,255,0.98); z-index: 99;
  flex-direction: column; align-items: center; justify-content: center; gap: 28px;
}
.mobile-menu.open { display: flex; }
.mobile-menu .nav-link { font-size: 1.2rem; }

@media (max-width: 900px) {
  .nav-links { display: none; }
  .hamburger { display: block; }
}

@media (max-width: 420px) {
  .nav-brand { font-size: 1.15rem; line-height: 1.1; }
  .nav-cta { padding: 7px 16px; font-size: 0.8rem; }
}

/* ─── HERO ─── */
.hero {
  min-height: 92vh; display: flex; align-items: center;
  background: linear-gradient(165deg, #fff 0%, var(--baby-blue-light) 40%, var(--cream) 100%);
  position: relative; overflow: hidden; padding-top: 72px;
}
.hero::before {
  content: ''; position: absolute; top: -120px; right: -200px;
  width: 600px; height: 600px; border-radius: 50%;
  background: radial-gradient(circle, rgba(142,176,39,0.08) 0%, transparent 70%);
}
.hero::after {
  content: ''; position: absolute; bottom: -80px; left: -100px;
  width: 400px; height: 400px; border-radius: 50%;
  background: radial-gradient(circle, rgba(204,229,247,0.4) 0%, transparent 70%);
}
.hero-inner {
  max-width: 1200px; margin: 0 auto; padding: 28px 24px 36px;
  display: grid; grid-template-columns: 1.2fr 0.8fr; gap: 76px; align-items: center;
  position: relative; z-index: 1;
}
.hero-text .hero-name {
  font-family: var(--serif); font-size: clamp(2rem, 4.5vw, 3.2rem);
  font-weight: 700; color: #000; margin-bottom: 0px; letter-spacing: -0.02em;
  line-height: 1.1;
}
.hero-text .hero-credentials {
  font-size: 19px; margin-bottom: 12px; font-family: var(--serif);
}
.hero-text .hero-address {
  font-size: 18px; color: var(--text-light); margin-bottom: 6px;
}
.hero-text .hero-address a {
  color: inherit; text-decoration: none; transition: color 0.2s;
}
.hero-text .hero-address a:hover {
  color: var(--olive);
}
.hero-text .hero-mode {
  font-size: 18px; color: var(--olive); font-weight: 600; margin-bottom: 20px;
}
.hero-text h1 {
  font-family: var(--serif); font-size: clamp(2.4rem, 5vw, 3.6rem);
  font-weight: 400; line-height: 1.15; color: #000;
  margin-bottom: 24px; letter-spacing: -0.02em;
}
.hero-text h1 em { font-style: italic; color: var(--olive); font-weight: 500; }
.hero-text > .section-label {
  font-size: 18px !important; margin-bottom: 20px !important;
}
.hero-text p {
  font-size: 19px; color: #000; max-width: 560px;
  margin-bottom: 32px; line-height: 1.75;
}
.hero-photo { position: relative; display: flex; justify-content: flex-end; transform: translateX(22px); }
.hero-photo-frame {
  width: 380px; height: 380px; border-radius: 20px;
  overflow: hidden; position: relative;
  background: linear-gradient(135deg, var(--baby-blue) 0%, var(--cream) 100%);
  box-shadow: 0 20px 60px rgba(0,0,0,0.08);
}
.hero-photo-frame img { width: 100%; height: 100%; object-fit: cover; }

@media (max-width: 768px) {
  .hero-inner { grid-template-columns: 1fr; text-align: center; gap: 40px; }
  .hero-text p { margin-left: auto; margin-right: auto; }
  .hero-photo { justify-content: center; transform: none; }
  .hero-photo-frame { width: 280px; height: 280px; margin: 0 auto; }
}

/* ─── BUTTONS ─── */
.btn-primary {
  display: inline-flex; align-items: center; gap: 8px;
  background: var(--olive); color: #fff; padding: 14px 32px;
  border-radius: 32px; font-size: 0.95rem; font-weight: 600;
  border: none; cursor: pointer; font-family: var(--sans);
  transition: all 0.3s; text-decoration: none;
}
.btn-primary:hover { background: var(--olive-dark); transform: translateY(-2px); box-shadow: 0 6px 20px rgba(142,176,39,0.3); }

.btn-outline {
  display: inline-flex; align-items: center; gap: 8px;
  background: transparent; color: var(--olive); padding: 12px 28px;
  border-radius: 32px; font-size: 0.92rem; font-weight: 600;
  border: 2px solid var(--olive); cursor: pointer; font-family: var(--sans);
  transition: all 0.3s; text-decoration: none;
}
.btn-outline:hover { background: var(--olive); color: #fff; }

/* ─── SECTIONS ─── */
.section { padding: 80px 24px; max-width: 1200px; margin: 0 auto; }
.section-full { padding: 80px 24px; }
.section-blue { background: var(--baby-blue-light); }
.section-cream { background: var(--cream); }
.section-olive-light { background: rgba(142,176,39,0.06); }

.section-header { text-align: center; margin-bottom: 56px; }
.section-header h2 {
  font-family: var(--serif); font-size: clamp(1.8rem, 3.5vw, 2.6rem);
  font-weight: 400; color: #000; margin-bottom: 16px; letter-spacing: -0.01em;
}
.section-header p {
  font-size: 1.05rem; color: #000; max-width: 600px; margin: 0 auto; line-height: 1.75;
}
.section-label {
  font-size: 0.78rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.15em; color: var(--olive); margin-bottom: 12px;
}

/* ─── QUOTE BLOCK ─── */
.quote-section {
  padding: 80px 24px;
  background: linear-gradient(135deg, var(--baby-blue-light) 0%, #fff 50%, var(--cream) 100%);
  text-align: center; position: relative;
}
.quote-section blockquote {
  font-family: var(--serif); font-size: clamp(1.3rem, 2.5vw, 1.8rem);
  font-style: italic; color: #000; max-width: 700px; margin: 0 auto 16px;
  line-height: 1.6; font-weight: 300;
}
.quote-section cite {
  font-size: 0.9rem; color: var(--text-light); font-style: normal;
}

/* ─── SERVICE TILES ─── */
.services-grid {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;
}
@media (min-width: 1160px) {
  .services-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
.service-tile {
  padding: 32px 28px; border-radius: 16px;
  background: #fff; border: 1px solid rgba(0,0,0,0.06);
  transition: all 0.3s; cursor: pointer; text-decoration: none; color: inherit;
}
.service-tile:hover {
  border-color: var(--olive);
  box-shadow: 0 8px 30px rgba(142,176,39,0.1);
  transform: translateY(-3px);
}
.service-tile-icon {
  width: 48px; height: 48px; border-radius: 12px;
  background: var(--baby-blue-light); display: flex;
  align-items: center; justify-content: center;
  margin-bottom: 18px; color: var(--olive);
}
.service-tile h3 {
  font-family: var(--serif); font-size: 1.25rem; font-weight: 600;
  color: #000; margin-bottom: 10px;
}
.service-tile p { font-size: 0.92rem; color: #000; line-height: 1.65; }

/* ─── MEET DR STEINGOLD ─── */
.meet-section {
  display: grid; grid-template-columns: 1fr 1.2fr; gap: 60px;
  align-items: center;
}
.meet-photo {
  border-radius: 20px; overflow: hidden; aspect-ratio: 1/1;
  background: linear-gradient(135deg, var(--baby-blue) 0%, var(--cream) 100%);
  box-shadow: 0 12px 40px rgba(0,0,0,0.08);
}
.meet-photo.about-intro-photo {
  width: 75%; margin: 0; justify-self: end;
}
.meet-photo img { width: 100%; height: 100%; object-fit: cover; }
.meet-text h2 {
  font-family: var(--serif); font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 400; color: #000;
}
.meet-text p { font-size: 1rem; color: #000; margin-bottom: 16px; line-height: 1.75; }

@media (max-width: 768px) {
  .meet-section { grid-template-columns: 1fr; }
  .meet-photo { max-width: 400px; margin: 0 auto; }
  .meet-photo.about-intro-photo { width: 100%; margin: 0 auto; justify-self: center; }
}

/* ─── TESTIMONIALS ─── */
.testimonial-card {
  background: #fff; border-radius: 16px; padding: 36px 32px;
  border: 1px solid rgba(0,0,0,0.06); position: relative;
  transition: all 0.3s;
}
.testimonial-card::before {
  content: '"'; position: absolute; top: 16px; left: 24px;
  font-family: var(--serif); font-size: 4rem; color: var(--olive);
  opacity: 0.2; line-height: 1;
}
.testimonial-card p {
  font-size: 0.95rem; color: #000; line-height: 1.75;
  font-style: italic; margin-bottom: 16px; position: relative; z-index: 1;
}
.testimonial-card .author {
  font-size: 0.85rem; font-weight: 700; color: var(--olive);
  font-style: normal;
}

.testimonials-grid {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
}

/* Pull quote */
.pull-quote {
  padding: 48px 32px; text-align: center;
  background: var(--cream); border-radius: 20px;
  margin: 40px 0; border-left: 4px solid var(--olive);
}
.pull-quote p {
  font-family: var(--serif); font-size: 1.15rem; font-style: italic;
  color: #000; max-width: 600px; margin: 0 auto 12px; line-height: 1.7;
}
.pull-quote .author { font-size: 0.85rem; color: var(--olive); font-weight: 600; }

/* ─── BLOG CARDS ─── */
.blog-grid {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 28px;
}
.blog-card {
  border-radius: 16px; overflow: hidden;
  background: #fff; border: 1px solid rgba(0,0,0,0.06);
  transition: all 0.3s; cursor: pointer;
}
.blog-card:hover { transform: translateY(-4px); box-shadow: 0 12px 40px rgba(0,0,0,0.08); }
.blog-card-img {
  height: 200px; background: linear-gradient(135deg, var(--baby-blue) 0%, var(--cream) 100%);
  display: flex; align-items: center; justify-content: center; font-size: 2.5rem;
}
.blog-card-body { padding: 24px; }
.blog-card-date { font-size: 0.8rem; color: var(--olive); font-weight: 600; margin-bottom: 8px; }
.blog-card-body h3 {
  font-family: var(--serif); font-size: 1.2rem; font-weight: 600;
  color: #000; margin-bottom: 10px; line-height: 1.35;
}
.blog-card-body p { font-size: 0.9rem; color: #000; line-height: 1.65; }
.blog-post-section { position: relative; }
.blog-back-btn { margin-bottom: 20px; }
@media (min-width: 1024px) {
  .blog-back-btn {
    position: absolute;
    left: -190px;
    top: 20px;
    margin-bottom: 0;
  }
}

/* ─── FAQ ─── */
.faq-item { border-bottom: 1px solid rgba(0,0,0,0.08); }
.faq-q {
  width: 100%; padding: 20px 0; display: flex;
  justify-content: space-between; align-items: center;
  background: none; border: none; cursor: pointer;
  font-family: var(--sans); font-size: 1rem; font-weight: 600;
  color: #000; text-align: left; gap: 16px;
}
.faq-q:hover { color: var(--olive); }
.faq-arrow {
  font-size: 1.2rem; transition: transform 0.3s;
  flex-shrink: 0; color: var(--olive);
}
.faq-arrow.open { transform: rotate(180deg); }
.faq-a { max-height: 0; overflow: hidden; transition: max-height 0.4s ease, padding 0.3s; }
.faq-a.open { max-height: 300px; }
.faq-a-inner { padding: 0 0 20px 0; font-size: 0.95rem; color: #000; line-height: 1.75; }

/* ─── PAGE HERO ─── */
.page-hero {
  padding: 140px 24px 60px; text-align: center;
  background: linear-gradient(165deg, #fff 0%, var(--baby-blue-light) 100%);
}
.page-hero h1 {
  font-family: var(--serif); font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 400; color: #000; margin-bottom: 16px;
}
.page-hero p {
  font-size: 1.05rem; color: #000; max-width: 600px;
  margin: 0 auto; line-height: 1.75;
}

/* ─── FORM ─── */
.form-group { margin-bottom: 20px; }
.form-group label {
  display: block; font-size: 0.88rem; font-weight: 600;
  color: #000; margin-bottom: 6px;
}
.form-input {
  width: 100%; padding: 14px 16px; border-radius: 10px;
  border: 1.5px solid rgba(0,0,0,0.12); font-size: 0.95rem;
  font-family: var(--sans); color: #000; background: #fff;
  transition: border-color 0.2s;
}
.form-input:focus { outline: none; border-color: var(--olive); }
.form-input::placeholder { color: rgba(0,0,0,0.35); }
textarea.form-input { resize: vertical; min-height: 120px; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
@media (max-width: 600px) { .form-row { grid-template-columns: 1fr; } }

.checkbox-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 0; gap: 16px;
}
.checkbox-row label { font-size: 0.95rem; color: #000; font-weight: 500; cursor: pointer; }
.custom-checkbox {
  width: 22px; height: 22px; border-radius: 6px;
  border: 2px solid rgba(0,0,0,0.2); cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.2s; flex-shrink: 0; background: #fff;
}
.custom-checkbox.checked { background: var(--olive); border-color: var(--olive); }
.custom-checkbox.checked::after { content: '✓'; color: #fff; font-size: 0.75rem; font-weight: 700; }

/* ─── FOOTER ─── */
.footer {
  background: #1a1a1a; color: #fff; padding: 60px 24px 32px;
}
.footer-inner {
  max-width: 1200px; margin: 0 auto;
  display: grid; grid-template-columns: 1.5fr 1fr 1fr; gap: 48px;
}
.footer h3 {
  font-family: var(--serif); font-size: 1.3rem; font-weight: 500;
  margin-bottom: 16px; color: #ffffff;
}
.footer p, .footer a {
  color: #ffffff; font-size: 0.9rem; line-height: 1.7;
  text-decoration: none;
}
.footer a:hover { color: var(--olive-light); }
.footer-nav { display: flex; flex-direction: column; gap: 8px; }
.footer-nav a { cursor: pointer; }
.footer-bottom {
  max-width: 1200px; margin: 40px auto 0; padding-top: 24px;
  border-top: 1px solid rgba(255,255,255,0.1);
  text-align: center; font-size: 0.82rem; color: #ffffff;
}
.logout-bar { text-align: center; padding: 12px; background: #111; }
.logout-bar button {
  background: none; border: 1px solid rgba(255,255,255,0.2);
  color: rgba(255,255,255,0.5); padding: 6px 20px; border-radius: 20px;
  font-size: 0.8rem; cursor: pointer; font-family: var(--sans);
}
.logout-bar button:hover { border-color: var(--olive); color: var(--olive); }

@media (max-width: 768px) {
  .footer-inner { grid-template-columns: 1fr; gap: 32px; }
}

/* ─── EXPANDABLE ─── */
.expandable { margin-bottom: 16px; border-radius: 12px; overflow: hidden; border: 1px solid rgba(0,0,0,0.08); }
.expandable-header {
  width: 100%; padding: 20px 24px; display: flex;
  justify-content: space-between; align-items: center;
  background: #fff; border: none; cursor: pointer;
  font-family: var(--serif); font-size: 1.1rem; font-weight: 600;
  color: #000; text-align: left;
}
.expandable-header:hover { background: var(--cream); }
.expandable-body { max-height: 0; overflow: hidden; transition: max-height 0.4s ease; }
.expandable-body.open { max-height: 800px; }
.expandable-body-inner { padding: 0 24px 24px; font-size: 0.95rem; color: #000; line-height: 1.75; }
.child-expandable .expandable-body-inner {
  font-size: 1.125rem;
  line-height: 1.8;
  padding: 8px 24px 28px;
}
.child-expandable .expandable-body-inner p { margin: 0 0 14px; }
.child-quote {
  font-family: var(--serif);
  font-style: italic;
  font-size: 1.42rem;
  line-height: 1.5;
  color: #000;
  margin-bottom: 16px !important;
}
.child-question-box {
  background: rgba(142,176,39,0.08);
  border-left: 3px solid var(--olive);
  border-radius: 10px;
  padding: 12px 14px;
  margin: 10px 0 18px;
}
.child-question-box p {
  font-style: italic;
  font-weight: 500;
  margin: 0 0 10px !important;
}
.child-question-box p:last-child { margin-bottom: 0 !important; }

/* ─── THERAPY CARDS ─── */
.therapy-card {
  padding: 32px; border-radius: 16px; background: #fff;
  border: 1px solid rgba(0,0,0,0.06); margin-bottom: 20px;
}
.therapy-card h3 {
  font-family: var(--serif); font-size: 1.3rem; font-weight: 600;
  color: #000; margin-bottom: 12px;
}
.therapy-card p { font-size: 0.95rem; color: #000; line-height: 1.75; }

/* ─── TIMELINE ─── */
.timeline { position: relative; padding-left: 32px; }
.timeline::before {
  content: ''; position: absolute; left: 6px; top: 8px; bottom: 8px;
  width: 2px; background: linear-gradient(to bottom, var(--olive), var(--baby-blue));
}
.timeline-item { position: relative; margin-bottom: 32px; }
.timeline-item::before {
  content: ''; position: absolute; left: -32px; top: 8px;
  width: 14px; height: 14px; border-radius: 50%;
  background: var(--olive); border: 3px solid #fff;
  box-shadow: 0 0 0 2px var(--olive);
}
.timeline-item h3 {
  font-family: var(--serif-display); font-size: 1.15rem; font-weight: 600;
  color: #000; margin-bottom: 4px;
  font-variant-numeric: lining-nums;
  font-feature-settings: "lnum";
}
.timeline-item .loc {
  font-family: Georgia, serif; font-size: 0.98rem; color: var(--olive);
  font-weight: 400; margin-bottom: 8px;
}
.timeline-item p { font-size: 0.92rem; color: #000; line-height: 1.7; }

/* ─── FEES ─── */
.fee-card {
  padding: 24px 20px; border-radius: 16px; background: #fff;
  border: 1px solid rgba(0,0,0,0.06); text-align: center;
}
.fee-card h3 { font-family: var(--serif); font-size: 1.1rem; margin-bottom: 8px; color: #000; }
.fee-card .price { font-family: var(--sans); font-size: 0.95rem; font-weight: 600; color: #000; }
.fee-card .detail { font-size: 0.95rem; color: #000; margin-top: 4px; }

/* ─── EXPERTISE GRID ─── */
.expertise-grid {
  display: flex; flex-wrap: wrap; gap: 10px; justify-content: center;
}
.expertise-tag {
  padding: 8px 20px; border-radius: 24px; font-size: 0.88rem;
  background: #fff; color: #000; font-weight: 500;
  border: 1px solid rgba(0,0,0,0.08);
}

/* ─── ADMIN ─── */
.admin-login {
  min-height: 100vh; display: flex; align-items: center;
  justify-content: center; padding-top: 72px; background: var(--cream);
}
.admin-box {
  background: #fff; padding: 48px; border-radius: 20px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.06); width: 100%; max-width: 400px;
}
.admin-box h2 {
  font-family: var(--serif); font-size: 1.6rem; margin-bottom: 24px;
  text-align: center; color: #000;
}

/* Admin Dashboard */
.admin-dash { padding: 100px 24px 60px; max-width: 1000px; margin: 0 auto; }
.admin-card {
  background: #fff; border-radius: 16px; padding: 24px;
  border: 1px solid rgba(0,0,0,0.06); margin-bottom: 16px;
}
.admin-tabs {
  display: flex; gap: 8px; margin-bottom: 32px; flex-wrap: wrap;
}
.admin-tab {
  padding: 10px 24px; border-radius: 24px; border: 1.5px solid rgba(0,0,0,0.12);
  background: #fff; cursor: pointer; font-size: 0.88rem; font-weight: 600;
  color: #000; font-family: var(--sans); transition: all 0.2s;
}
.admin-tab.active, .admin-tab:hover {
  background: var(--olive); color: #fff; border-color: var(--olive);
}

/* ─── BLOG EDITOR ─── */
.editor-bar {
  display: flex; gap: 8px; padding: 12px; background: var(--cream);
  border-radius: 10px; margin-bottom: 12px; flex-wrap: wrap;
}
.editor-btn {
  padding: 6px 12px; border-radius: 6px; border: 1px solid rgba(0,0,0,0.12);
  background: #fff; cursor: pointer; font-size: 0.82rem; font-weight: 600;
  color: #000; font-family: var(--sans); transition: all 0.2s;
}
.editor-btn:hover { border-color: var(--olive); color: var(--olive); }

.rich-editor {
  min-height: 220px; padding: 14px 16px; border-radius: 10px;
  border: 1.5px solid rgba(0,0,0,0.12); font-size: 0.95rem;
  font-family: var(--sans); color: #000; background: #fff; line-height: 1.7;
  direction: ltr; text-align: left;
}
.rich-editor:focus { outline: none; border-color: var(--olive); }
.rich-editor p { margin: 0 0 14px; }
.rich-editor h2, .blog-content h2 { font-family: var(--serif); font-size: 1.7rem; margin: 12px 0; }
.rich-editor h3, .blog-content h3 { font-family: var(--serif); font-size: 1.35rem; margin: 10px 0; }
.blog-content p { color: #000; line-height: 1.8; margin-bottom: 18px; font-size: 1.05rem; }
.blog-content ul, .blog-content ol { margin: 8px 0 18px 24px; }
.blog-content blockquote {
  border-left: 3px solid var(--olive);
  padding-left: 14px; margin: 8px 0 18px; color: #000; font-style: italic;
}

.link-popover {
  position: absolute; z-index: 100; background: #fff; border: 1.5px solid rgba(0,0,0,0.15);
  border-radius: 10px; padding: 14px 16px; box-shadow: 0 6px 24px rgba(0,0,0,0.13);
  display: flex; flex-direction: column; gap: 8px; min-width: 280px;
}
.link-popover::after {
  content: ""; position: absolute; bottom: -6px; left: 24px;
  width: 10px; height: 10px; background: #fff; border-right: 1.5px solid rgba(0,0,0,0.15);
  border-bottom: 1.5px solid rgba(0,0,0,0.15); transform: rotate(45deg);
}
.link-popover input {
  padding: 7px 10px; border-radius: 6px; border: 1.5px solid rgba(0,0,0,0.12);
  font-size: 0.85rem; font-family: var(--sans); width: 100%; box-sizing: border-box;
}
.link-popover input:focus { outline: none; border-color: var(--olive); }
.link-popover-actions { display: flex; gap: 8px; margin-top: 4px; }
.link-popover-actions button {
  padding: 5px 14px; border-radius: 6px; border: 1px solid rgba(0,0,0,0.12);
  font-size: 0.8rem; font-weight: 600; cursor: pointer; font-family: var(--sans);
}

/* ─── ANIMATIONS ─── */
.fade-in { opacity: 0; transform: translateY(20px); animation: fadeIn 0.6s ease forwards; }
@keyframes fadeIn { to { opacity: 1; transform: translateY(0); } }
.fade-in-d1 { animation-delay: 0.1s; }
.fade-in-d2 { animation-delay: 0.2s; }
.fade-in-d3 { animation-delay: 0.3s; }
.fade-in-d4 { animation-delay: 0.4s; }

/* ─── IMAGE PLACEHOLDER ─── */
.img-placeholder {
  width: 100%; height: 100%;
  background: linear-gradient(135deg, var(--baby-blue) 0%, var(--cream) 100%);
  display: flex; align-items: center; justify-content: center;
  color: rgba(0,0,0,0.2); font-size: 0.8rem; text-align: center; padding: 16px;
}

/* Misc */
.text-olive { color: var(--olive); }
.max-w-800 { max-width: 800px; margin-left: auto; margin-right: auto; }
.max-w-1200 { max-width: 1200px; margin-left: auto; margin-right: auto; }
.mt-40 { margin-top: 40px; }
.mt-60 { margin-top: 60px; }
.mb-32 { margin-bottom: 32px; }
.mb-48 { margin-bottom: 48px; }
.text-center { text-align: center; }
.grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; align-items: start; }
@media (max-width: 768px) { .grid-2 { grid-template-columns: 1fr; } }
`;

// ─── ICON COMPONENTS ────────────────────────────────────────
const Icons = {
  Brain: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a7 7 0 0 0-7 7c0 3 1.5 5 3 6.5V20a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-4.5c1.5-1.5 3-3.5 3-6.5a7 7 0 0 0-7-7z"/><path d="M9 22v-2m6 0v2M12 2v2"/></svg>,
  Heart: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
  Shield: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  Users: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  Star: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  Child: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="5" r="3"/><path d="M12 8v4m-4 4l4-4 4 4m-4 0v4"/></svg>,
  Target: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
  Scale: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 3v18M3 7l3 5h12l3-5"/><circle cx="6" cy="12" r="2"/><circle cx="18" cy="12" r="2"/></svg>,
  Phone: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  Mail: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  MapPin: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  Arrow: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
  Chevron: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>,
  Pen: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  Trash: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  Link: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  CheckCircle: () => <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--olive)" strokeWidth="1.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  Lock: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  Eye: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  Archive: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3" width="20" height="5" rx="1"/><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"/><line x1="10" y1="13" x2="14" y2="13"/></svg>,
  Restore: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><polyline points="3 3 3 8 8 8"/></svg>,
  BookOpen: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>,
};

function ServiceIcon({ name }) {
  const map = { brain: Icons.Brain, heart: Icons.Heart, users: Icons.Users, child: Icons.Child, target: Icons.Target, scale: Icons.Scale };
  const Comp = map[name] || Icons.Star;
  return <Comp />;
}

// ─── SHARED COMPONENTS ──────────────────────────────────────
function Navbar() {
  const { page, go, linkProps } = useRoute();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showHomeCta, setShowHomeCta] = useState(true);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    if (page !== "/") {
      setShowHomeCta(true);
      return;
    }
    const hero = document.getElementById("home-hero");
    if (!hero) {
      setShowHomeCta(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => setShowHomeCta(!entry.isIntersecting),
      { threshold: 0.2 }
    );
    obs.observe(hero);
    return () => obs.disconnect();
  }, [page]);

  const links = [
    { label: "About", path: "/about" },
    { label: "Services & Approach", path: "/services" },
    { label: "Child Psychology", path: "/child-psychology" },
    { label: "Testimonials", path: "/testimonials" },
    { label: "Blog", path: "/blog" },
    { label: "Contact", path: "/contact" },
  ];

  const navProps = (p) => {
    const lp = linkProps(p);
    return { ...lp, onClick: (e) => { lp.onClick(e); if (e.defaultPrevented) setMobileOpen(false); } };
  };

  return (
    <>
      <nav className={`nav ${scrolled ? "scrolled" : ""}`}>
        <div className="nav-inner">
          <a className="nav-brand" {...navProps("/")} aria-label={`${SITE_NAME} home`}>
            Dr. Steingold <span>Psychology</span>
          </a>
          <div className="nav-links">
            {links.map(l => (
              <a key={l.path} className={`nav-link ${page === l.path ? "active" : ""}`} {...navProps(l.path)} aria-current={page === l.path ? "page" : undefined}>{l.label}</a>
            ))}
            <a className={`nav-cta ${!showHomeCta ? "hidden" : ""}`} {...navProps("/contact")}>Request Appointment</a>
          </div>
          <button className={`hamburger ${mobileOpen ? "open" : ""}`} onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
            <span /><span /><span />
          </button>
        </div>
      </nav>
      <div className={`mobile-menu ${mobileOpen ? "open" : ""}`}>
        {links.map(l => (
          <a key={l.path} className="nav-link" {...navProps(l.path)} aria-current={page === l.path ? "page" : undefined}>{l.label}</a>
        ))}
        <a className={`nav-cta ${!showHomeCta ? "hidden" : ""}`} {...navProps("/contact")} style={{marginTop: 8}}>Request Appointment</a>
      </div>
    </>
  );
}

function Footer() {
  const { go, linkProps } = useRoute();
  const { loggedIn, logout } = useAuth();
  return (
    <>
      <footer className="footer">
        <div className="footer-inner">
          <div>
            <h3>Dr. Steingold Psychology</h3>
            <p>Compassionate, professional psychology services for adults and children in New York City.</p>
            <p style={{ marginTop: 12, fontSize: "0.82rem" }}>Dr. Steingold Psychology P.C.</p>
          </div>
          <div>
            <h3>Navigate</h3>
            <div className="footer-nav">
              <a {...linkProps("/about")}>About Dr. Steingold</a>
              <a {...linkProps("/services")}>Services</a>
              <a {...linkProps("/child-psychology")}>Child Psychology</a>
              <a {...linkProps("/testimonials")}>Testimonials</a>
            </div>
          </div>
          <div>
            <h3>Resources</h3>
            <div className="footer-nav">
              <a {...linkProps("/blog")}>Blog</a>
              <a {...linkProps("/contact#faq")}>FAQ</a>
              <a {...linkProps("/contact")}>Contact Us</a>
              <a {...linkProps("/contact")}>Request Appointment</a>
            </div>
          </div>
          <div>
            <h3>Contact</h3>
            <p style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}><Icons.Phone /><a href={`tel:${PHONE.replace(/\D/g, '')}`} style={{ color: "inherit", textDecoration: "none", cursor: "pointer" }}>{PHONE}</a></p>
            <p style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}><Icons.Mail /><a href={`mailto:${EMAIL}`} style={{ color: "inherit", textDecoration: "none", cursor: "pointer" }}>{EMAIL}</a></p>
            <p style={{ display: "flex", alignItems: "center", gap: 8 }}><Icons.MapPin /><a href="https://maps.app.goo.gl/MDN8Py2n46C567cx8" target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "none", cursor: "pointer" }}>{ADDRESS}</a></p>
          </div>
        </div>
        <div className="footer-bottom">© 2026 Dr. Steingold Psychology P.C. All rights reserved.</div>
      </footer>
      {loggedIn && (
        <div className="logout-bar">
          <button onClick={logout}>Logout (Admin)</button>
        </div>
      )}
    </>
  );
}

function FAQ({ className = "", id }) {
  const [open, setOpen] = useState(null);
  return (
    <div className={className} id={id}>
      <div className="section-header">
        <p className="section-label">FAQ</p>
        <h2>Frequently Asked Questions</h2>
      </div>
      <div className="max-w-800">
        {FAQ_DATA.map((item, i) => (
          <div key={i} className="faq-item">
            <button className="faq-q" onClick={() => setOpen(open === i ? null : i)}>
              {item.q}
              <span className={`faq-arrow ${open === i ? "open" : ""}`}><Icons.Chevron /></span>
            </button>
            <div className={`faq-a ${open === i ? "open" : ""}`}>
              <div className="faq-a-inner" dangerouslySetInnerHTML={{ __html: item.a }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TestimonialCard({ t }) {
  return (
    <div className="testimonial-card">
      <p>{t.text}</p>
      <div className="author">— {t.author}</div>
    </div>
  );
}

function CTA({ text = "Ready to take the next step?", body = "Your path to healing is unique. Contact me today to discuss your needs and how we can work together." }) {
  const { go } = useRoute();
  return (
    <div className="text-center" style={{ padding: "36px 0" }}>
      <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.8rem", fontWeight: 400, color: "#000", marginBottom: 16 }}>{text}</h2>
      <p style={{ color: "#000", marginBottom: 28, maxWidth: 500, margin: "0 auto 28px" }}>
        {body}
      </p>
      <button className="btn-primary" onClick={() => go("/contact")}>Request an Appointment <Icons.Arrow /></button>
    </div>
  );
}

function Expandable({ title, children, className = "", defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`expandable ${className}`.trim()}>
      <button className="expandable-header" onClick={() => setOpen(!open)}>
        {title}
        <span className={`faq-arrow ${open ? "open" : ""}`}><Icons.Chevron /></span>
      </button>
      <div className={`expandable-body ${open ? "open" : ""}`}>
        <div className="expandable-body-inner">{children}</div>
      </div>
    </div>
  );
}

function Img({ url, style = {} }) {
  return <img src={url} alt="Dr. Eugenia Steingold" style={{ width: "100%", height: "100%", objectFit: "cover", ...style }} />;
} 

function ImgPlaceholder({ text, style = {} }) {
  return <div className="img-placeholder" style={style}>{text}</div>;
}

function DrPhoto({ style = {} }) {
  return <img src={DR_PHOTO} alt="Dr. Eugenia Steingold" style={{ width: "100%", height: "100%", objectFit: "cover", ...style }} />;
}


// ─── PAGE: HOME ─────────────────────────────────────────────
function HomePage() {
  const { go, linkProps } = useRoute();
  // No title override → homepage uses the name-led default
  // ("Dr. Eugenia Steingold, PhD — Manhattan Clinical Psychologist").
  useSeo({ path: "/" });
  const [blogs, setBlogs] = useState(null);

  useEffect(() => {
    let active = true;
    api.getPublicBlogs()
      .then((resp) => {
        if (active) setBlogs(Array.isArray(resp.blogs) ? resp.blogs : []);
      })
      .catch(() => { if (active) setBlogs([]); });
    return () => { active = false; };
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="hero" id="home-hero">
        <div className="hero-inner">
          <div className="hero-text fade-in">
            <div className="hero-name">Dr. Eugenia Steingold</div>
            <div className="hero-credentials">Ph.D., Psychology, Princeton University</div>
            <div className="hero-address"><a href={MAPS_URL} target="_blank" rel="noopener noreferrer">Manhattan Location: {ADDRESS}</a></div>
            <div className="hero-mode">In person and virtual appointments</div>
            <p className="section-label">NYC Psychologist for Adults and Children</p>
            <h1>Find your way back to <em>yourself</em></h1>
            <p>Welcome. I'm Dr. Eugenia Steingold, a board-certified psychotherapist in NYC. I have over 30 years of experience in helping adults, children, and families navigate life's challenges to find growth, healing, and fulfillment.
            </p>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <button className="btn-primary" onClick={() => go("/contact")}>Request Appointment <Icons.Arrow /></button>
              <button className="btn-outline" onClick={() => go("/about")}>Meet Dr. Steingold</button>
            </div>
          </div>
          <div className="hero-photo fade-in fade-in-d2">
            <div className="hero-photo-frame">
              <DrPhoto />
            </div>
          </div>
        </div>
      </section>

      {/* Motivational */}
      <section className="quote-section">
        <div className="max-w-800 fade-in">
          <p className="section-label">A Message From Dr. Steingold</p>
          <blockquote style={{ fontFamily: "var(--serif)", fontSize: "clamp(1.1rem, 2.2vw, 1.55rem)", fontStyle: "italic", color: "#000", lineHeight: 1.7, marginBottom: 32 }}>
            Do you want to like yourself again? Do you want to feel excited about your life?
            Do you want to be unafraid of your future? Do you feel stuck, continuously affected by past trauma?
          </blockquote>
          <p style={{ fontSize: "clamp(0.98rem, 1.65vw, 1.18rem)", color: "#000", lineHeight: 1.72, maxWidth: 650, margin: "0 auto 20px" }}>
            <strong>If you answered yes to any of these questions, you will benefit from psychotherapy.</strong><br/>
            Negative statements like "I am stuck, I don't like my life" easily become internalized.
            The good news? <i>That is not your real Self talking.</i>
          </p>
          <p style={{ fontSize: "clamp(0.98rem, 1.65vw, 1.18rem)", color: "#000", lineHeight: 1.72, maxWidth: 650, margin: "0 auto 32px" }}>
            Psychotherapy will help you relieve and release past experiences completely.
            Instead of being stuck in the traumatic past, therapy will uncover and reveal your true Self; the one that is creative, hopeful, and full of life.
          </p>
          <button className="btn-primary" onClick={() => go("/contact")}>Ready to Start? <Icons.Arrow /></button>
        </div>
      </section>

      {/* Services Grid - clickable to services page */}
      <section className="section">
        <div className="section-header">
          <p className="section-label">How I Can Help</p>
          <h2>Areas of Specialization</h2>
        </div>
        <div className="services-grid">
          {HOME_SERVICES.map((s, i) => (
            <div key={i} className={`service-tile fade-in fade-in-d${i % 4 + 1}`} onClick={() => go(`/services#${s.sectionId}`)}>
              <div className="service-tile-icon"><ServiceIcon name={s.icon} /></div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-40">
          <button className="btn-outline" onClick={() => go("/services")}>View All Services <Icons.Arrow /></button>
        </div>
      </section>

      <section className="section-full section-cream">
        <div className="section" style={{ paddingTop: 0, paddingBottom: 0 }}>
          <div className="meet-section">
            <div className="meet-photo fade-in">
              <DrPhoto />
            </div>
            <div className="meet-text fade-in fade-in-d2">
              <h2>Meet Dr. Eugenia Steingold</h2>
              <p style={{ fontSize: "1rem", color: "#000", fontStyle: "italic", fontWeight: 400, marginBottom: 14, fontFamily: "var(--serif)" }}>
                Ph.D., Psychology, Princeton University
              </p>
              <p style={{ fontSize: "0.98rem", color: "var(--olive)", fontWeight: 600, marginBottom: 14 }}>
                NYS Licensed Psychologist, Board Certified Psychotherapist
              </p>
              <p>
                Psychology is my lifelong passion. For over 30 years, I have been dedicated to helping people
                get unstuck, break through traumas, and rediscover their potential for growth and self-acceptance.
              </p>
              <p>
                With a Ph.D. from Princeton University and postdoctoral training at Harvard,
                I bring a deep understanding of how the mind works and how it heals.
                I treat adults and children, working with anxiety, depression, trauma, ADHD, autism spectrum disorders, and more.
              </p>
              <p style={{ fontStyle: "italic", color: "var(--olive)", fontWeight: 500 }}>
                "My ultimate emotional reward is to see my clients breaking through their traumas and negative patterns."
              </p>
              <button className="btn-outline mt-40" onClick={() => go("/about")}>See Full Bio <Icons.Arrow /></button>
            </div>
          </div>
        </div>
      </section>

      {/* Child Psychology Preview */}
      <section className="section">
        <div className="meet-section" style={{ direction: "ltr" }}>
          <div className="fade-in" style={{ order: 2 }}>
            <div className="meet-photo" style={{ aspectRatio: "4/3" }}>
              <Img url="/media/child-1.webp" />
            </div>
          </div>
          <div className="fade-in fade-in-d2" style={{ order: 1 }}>
            <p className="section-label">Child Psychology</p>
            <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(1.4rem, 2.5vw, 1.9rem)", fontWeight: 400, color: "#000", marginBottom: 16 }}>
              NYC Child Psychology
            </h2>
            <p style={{ fontSize: "0.88rem", color: "var(--olive)", fontWeight: 600, marginBottom: 16, lineHeight: "1.4" }}>
              Dr. Eugenia Steingold<br />
              Ph.D., Psychology, Princeton University<br />
              NYS Licensed Psychologist, Board Certified Psychotherapist
            </p>
            <p style={{ color: "#000", marginBottom: 16, lineHeight: 1.75 }}>
              Does your child ignore you? Does your child have meltdowns or aggressive outbursts?
              Is your child struggling in school or withdrawing from friends?
              These can be signs that your child needs professional support.
            </p>
            <p style={{ color: "#000", lineHeight: 1.75, marginBottom: 24 }}>
              I specialize in treating ASD, ADHD, depression, anxiety, phobias, and a wide range of
              behavioral, emotional, and cognitive challenges in children. I am fully bilingual in English and Russian.
            </p>
            <button className="btn-outline" onClick={() => go("/child-psychology")}>Learn More <Icons.Arrow /></button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-full section-blue">
        <div className="section" style={{ paddingTop: 0, paddingBottom: 0 }}>
          <div className="section-header">
            <p className="section-label">What My Patients Say</p>
            <h2>Testimonials</h2>
          </div>
          <div className="testimonials-grid">
            {TESTIMONIALS_FULL.slice(0, 3).map((t, i) => (
              <TestimonialCard key={i} t={t} />
            ))}
          </div>
          <div className="text-center mt-40">
            <button className="btn-outline" onClick={() => go("/testimonials")}>Read More Testimonials <Icons.Arrow /></button>
          </div>
        </div>
      </section>

      {/* Chuck Palahniuk Quote */}
      <section className="quote-section" style={{ padding: "60px 24px" }}>
        <blockquote>
          "People fall so in love with their pain, they can't leave it behind.
          The same as the stories they tell. We trap ourselves."
        </blockquote>
        <cite>— Chuck Palahniuk</cite>
        <p style={{ fontFamily: "var(--serif)", fontSize: "1.6rem", fontWeight: 300, color: "var(--olive)", marginTop: 20 }}>
          Leave Your Pain Behind
        </p>
      </section>

      {/* Blog Preview */}
      <section className="section">
        <div className="section-header">
          <p className="section-label">Blog</p>
          <h2>Dr. Steingold's Insights</h2>
        </div>
        <div className="blog-grid">
          {blogs !== null && blogs.slice(0, 3).map((b) => (
            <a key={b.id} className="blog-card fade-in" style={{ color: "inherit", textDecoration: "none", display: "block" }} {...linkProps(`/blog/${b.slug}`)}>
              <div className="blog-card-img" style={{ position: "relative", overflow: "hidden" }}>
                {getBlogImageUrl(b)
                  ? <img src={getBlogImageUrl(b)} alt={b.title} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <ImgPlaceholder text={b.title} />}
              </div>
              <div className="blog-card-body">
                <div className="blog-card-date">{b.date}</div>
                <h3>{b.title}</h3>
                <p>{b.desc}</p>
              </div>
            </a>
          ))}
        </div>
        <div className="text-center mt-40">
          <button className="btn-outline" onClick={() => go("/blog")}>Read the Blog <Icons.Arrow /></button>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-full section-cream">
        <div className="section" style={{ paddingTop: 0, paddingBottom: 0 }}>
          <FAQ />
        </div>
      </section>
    </div>
  );
}


// ─── PAGE: ABOUT ────────────────────────────────────────────
function AboutPage() {
  const { go } = useRoute();
  useSeo({
    title: "About",
    description: "Meet Dr. Eugenia Steingold, PhD, a licensed clinical psychologist in Manhattan with expertise in psychotherapy, trauma-focused EMDR, and child & adolescent neuropsychological evaluation.",
    path: "/about",
    type: "profile",
  });
  return (
    <div>
      <section className="page-hero">
        <p className="section-label">About Dr. Steingold</p>
        <h1>Meet Dr. Eugenia Steingold</h1>
        <p style={{ color: "var(--olive)", fontWeight: 600, marginBottom: 10 }}>
          NYS Licensed Psychologist, Board Certified Psychotherapist
        </p>
        <p>My journey, my passion, and my commitment to your well-being.</p>
      </section>

      <section className="section">
        <div className="meet-section" style={{ alignItems: "start", columnGap: 69 }}>
          <div className="meet-photo about-intro-photo fade-in">
            <DrPhoto />
          </div>
          <div className="meet-text fade-in fade-in-d2">
            <h2>Eugenia Steingold, Ph.D</h2>
            <p style={{ fontSize: "0.98rem", color: "var(--olive)", fontWeight: 600, marginBottom: 12 }}>
              NYS Licensed Psychologist, Board Certified Psychotherapist
            </p>
            <p style={{ marginBottom: 10 }}>
              Psychology is my lifelong passion. For over 30 years, I have been dedicated to helping people get unstuck, break through traumas, and rediscover their potential for growth and self-acceptance. Whether it's anxiety, depression, trauma, or behavioral issues, my approach combines various therapeutic methods tailored to your individual needs. I believe in the power of understanding and connecting with each person to unlock their potential and support their growth.
            </p>
          </div>
        </div>
      </section>

      <section className="section-full section-cream">
        <div className="section-header">
          <h2>Education</h2>
        </div>
        <div className="section max-w-1200" style={{ paddingTop: 0, paddingBottom: 0 }}>
          <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.5rem", fontWeight: 600, marginBottom: 10 }}>My Main Discovery at Princeton</h3>
          <p style={{ marginBottom: 14, color: "#000", lineHeight: 1.8 }}>
            I studied Cognition (how people think and make decisions) at Princeton University. My main discovery there was that our thinking is almost always irrational, especially under stress, and our choices are almost never optimal. My thesis demonstrated how people are often irrational and fail to make choices that are best for them. This understanding forms the basis of my work in helping clients develop clearer, more effective ways of thinking.
          </p>
          <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.5rem", fontWeight: 600, marginBottom: 10 }}>My Main Discovery at Harvard</h3>
          <p style={{ marginBottom: 14, color: "#000", lineHeight: 1.8 }}>
            I studied Child Development at Harvard University, where I completed my Postdoctoral Training. My main discovery at Harvard was that we come into this world equipped with a variety of tools, faculties, and even innate knowledge that help us to learn, discover, and grow. Our potential is truly mind-blowing! This insight fuels my strength-based approach, focusing on the inherent capabilities within every child and adult.
          </p>
          <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.5rem", fontWeight: 600, marginBottom: 10 }}>A Lifelong Love for People's Stories</h3>
          <p style={{ marginBottom: 14, color: "#000", lineHeight: 1.8 }}>
            Before fancy schools like Princeton and Harvard, I attended the State University of New York at Stony Brook. My main discovery there was that I love people and their stories. Nothing fascinates me more! This curiosity and deep respect for each individual's journey is the heart of my practice.
          </p>
          <p style={{ color: "#000", lineHeight: 1.8 }}>
            My ultimate emotional reward is to see my clients breaking through their traumas and negative patterns. As a mother of three, I know the constant love and worry that every parent is consumed with. There is nothing more satisfying than knowing that you are entrusting your child in loving and professional hands.
          </p>
        </div>
      </section>

      {/* Professional Timeline */}
      <section className="section" style={{ paddingBottom: 44 }}>
        <div className="section-header">
          <h2>Professional Experience</h2>
        </div>
        <div className="max-w-800">
          <div className="timeline">
            <div className="timeline-item fade-in">
              <h3>Undergraduate Studies in Psychology</h3>
              <div className="loc">SUNY Stony Brook</div>
              <p>Undergraduate work in psychology at the State University of New York at Stony Brook.</p>
            </div>
            <div className="timeline-item fade-in fade-in-d1">
              <h3>Doctoral Program — Ph.D. in Psychology</h3>
              <div className="loc">Princeton University</div>
              <p>Doctoral program in psychology at Princeton University, specializing in psycholinguistics and cognitive psychology.</p>
            </div>
            <div className="timeline-item fade-in fade-in-d2">
              <h3>Postdoctoral Training in Child Development</h3>
              <div className="loc">Harvard University</div>
              <p>Three-year postdoctoral training in Child Development at Harvard University.</p>
            </div>
            <div className="timeline-item fade-in fade-in-d3">
              <h3>20+ Years Working with Children and Families in Therapeutic Settings</h3>
            </div>
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 36 }}>
        <CTA body="I am passionate about my patients' progress and development, and my patients' parents are necessary and invaluable partners in the process of treatment." />
      </section>
    </div>
  );
}

// ─── PAGE: SERVICES ─────────────────────────────────────────
function ServicesPage() {
  const { go } = useRoute();
  useSeo({
    title: "Services & Approach | Therapy, EMDR & Testing",
    description: "Psychotherapy, trauma-focused EMDR, family counseling, and neuropsychological testing for children and adults in Manhattan. In-person and virtual sessions with Dr. Eugenia Steingold.",
    path: "/services",
  });
  const [activeMethod, setActiveMethod] = useState(0);
  const methods = [
    { name: "Cognitive-Behavioral Therapy (CBT)", desc: "The best solution-based therapy. Let's look at your immediate problems and find the best solution by re-framing how you think about them. You will be thinking about your situation in and out of the box, until the optimal answer is on the table, in front of you." },
    { name: "Psychodynamic / Psychoanalytic Therapy", desc: "We explore the deeper emotional currents — early experiences, relationships, and unconscious patterns — that shape how you move through the world. This approach helps you understand the 'why' behind your feelings and behaviors." },
    { name: "Gestalt Therapy", desc: "Focusing on the present moment and your immediate experience. Gestalt therapy helps you become more aware of what you feel and do, empowering you to make choices that align with your true self." },
    { name: "Trauma-focused EMDR Therapy", desc: "Eye Movement Desensitization and Reprocessing (EMDR) is a powerful, evidence-based therapy specifically designed to help process and heal from traumatic experiences. It reduces the emotional charge of traumatic memories." },
    { name: "Family Counseling", desc: "Strengthening relationships through improved communication, deeper understanding, and the resolution of recurring conflicts. I work with families to build healthier, more fulfilling connections." },
    { name: "Neuropsychological Testing", desc: "Comprehensive evaluation to assess cognitive functioning, learning abilities, and developmental concerns. Available for children and adolescents ages 2–18. Results guide treatment recommendations and school accommodations." }
  ];

  // Hash scroll for service section links
  useEffect(() => {
    const hash = window.location.hash.split("#")[1];
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  }, []);

  return (
    <div>
      <section className="page-hero">
        <p className="section-label">Services & Approach</p>
        <h1>A Holistic, Integrated Approach to Therapy</h1>
        <p>Tailored to your unique needs, because therapy is not one-size-fits-all.</p>
      </section>

      {/* Initial Eval — compact */}
      <section className="section" style={{ padding: "52px 24px 28px" }}>
        <div className="max-w-800">
          <div className="section-header" style={{ marginBottom: 28 }}>
            <h2>How We Begin</h2>
          </div>
          <div className="therapy-card" style={{ marginBottom: 0 }}>
            <h3>Initial Evaluation & Diagnosis</h3>
            <p>
              Every therapeutic journey begins with understanding. During our first session (60 minutes),
              I conduct a thorough evaluation to understand your concerns, history, and goals.
              This may include diagnostic assessment, discussion of your current challenges,
              and initial recommendations for a personalized treatment plan.
            </p>
            <p style={{ marginTop: 12 }}>
              When appropriate, I collaborate with other healthcare providers, including psychiatrists, pediatricians,
            school counselors, and other specialists, to ensure comprehensive, coordinated care.
            </p>
          </div>
        </div>
      </section>

      {/* Therapeutic Methods — less padding */}
      <section className="section-full section-cream" style={{ padding: "48px 24px 28px" }}>
        <div className="section" style={{ paddingTop: 0, paddingBottom: 0 }}>
          <div className="section-header" style={{ marginBottom: 32 }}>
            <p className="section-label">My Therapeutic Toolbox</p>
            <h2>My Therapeutic Toolbox</h2>
            <p>Therapy is not one-size-fits-all. I draw from a variety of evidence-based modalities to create a personalized treatment plan that honors your individual journey.</p>
          </div>
          <div className="max-w-800">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 32, justifyContent: "center" }}>
              {methods.map((m, i) => (
                <button key={i} onClick={() => setActiveMethod(i)} style={{
                  padding: "10px 20px", borderRadius: 24, border: "1.5px solid",
                  borderColor: activeMethod === i ? OLIVE : "rgba(0,0,0,0.12)",
                  background: activeMethod === i ? OLIVE : "#fff",
                  color: activeMethod === i ? "#fff" : "#000",
                  fontSize: "0.88rem", fontWeight: 600, cursor: "pointer",
                  fontFamily: "var(--sans)", transition: "all 0.2s"
                }}>{m.name}</button>
              ))}
            </div>
            <div className="therapy-card" style={{ minHeight: 140 }}>
              <h3>{methods[activeMethod].name}</h3>
              <p>{methods[activeMethod].desc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Collaboration */}
      <section className="section-full section-cream" style={{ padding: "12px 24px 68px" }}>
        <div className="max-w-800">
          <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.35rem", marginBottom: 10, color: "#000" }}>Collaboration with Other Professionals</h3>
          <p style={{ color: "#000", lineHeight: 1.75 }}>
            When appropriate, I collaborate with other healthcare providers, including psychiatrists, pediatricians,
            school counselors, and other specialists, to ensure comprehensive, coordinated care.
            Your well-being is a team effort, and I work to make sure every piece of your support system is aligned.
          </p>
        </div>
      </section>

      {/* Areas of Expertise — less padding, white bg tags */}
      <section className="section-full section-blue" style={{ padding: "48px 24px" }} id="specialization">
        <div className="section" style={{ paddingTop: 0, paddingBottom: 0 }}>
          <div className="section-header" style={{ marginBottom: 32 }}>
            <p className="section-label">Specialization</p>
            <h2>Areas of Expertise</h2>
          </div>
          <div className="expertise-grid" id="anxiety-depression">
            {SERVICES_LIST.map((s, i) => <span key={i} className="expertise-tag" id={i === 0 ? "trauma-ptsd" : i === 2 ? "family" : i === 3 ? "child-psych" : i === 4 ? "exec-coaching" : i === 12 ? "weight-body" : undefined}>{s}</span>)}
          </div>
          <p className="text-center" style={{ marginTop: 24, fontSize: "0.92rem", color: "#000" }}>
            Therapy: Children and Adults &nbsp;|&nbsp; Neuropsychological evaluation: ages 2–18
          </p>
        </div>
      </section>

      {/* Fees — compact, same font size/color for prices */}
      <section className="section" style={{ paddingBottom: 40 }}>
        <div className="section-header" style={{ marginBottom: 32 }}>
          <p className="section-label">Sessions & Insurance</p>
          <h2>Sessions</h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, maxWidth: 600, margin: "0 auto" }}>
          <div className="fee-card">
            <h3>Initial Evaluation</h3>
            <div className="detail">60 minutes</div>
          </div>
          <div className="fee-card">
            <h3>Individual Therapy</h3>
            <div className="detail">45 minutes</div>
          </div>
        </div>
        <div className="max-w-800 mt-40">
          <p style={{ textAlign: "center", marginBottom: 24, fontSize: "0.95rem", color: "#000" }}>Most payment methods accepted (cards, checks, etc.)</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            <div className="therapy-card" style={{ marginBottom: 0 }}>
              <h3>In-Network Insurance</h3>
              <p>I am an in-network provider with Aetna. If you have an Aetna plan, your sessions may be fully or partially covered, depending on your specific benefits.</p>
            </div>
            <div className="therapy-card" style={{ marginBottom: 0 }}>
              <h3>Out-of-Network</h3>
              <p>For all other insurance plans, I am considered an out-of-network provider. We are happy to submit a claim to your insurance for your reimbursement on your behalf.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Hours */}
      <section className="section-full section-cream" style={{ padding: "40px 24px" }}>
        <div className="max-w-800 text-center">
          <h3 style={{ fontFamily: "var(--serif)", marginBottom: 12, color: "#000" }}>Office Hours</h3>
          <p style={{ color: "#000", fontSize: "0.95rem" }}>
            <strong>Monday:</strong> 10 AM – 7 PM &nbsp;&nbsp;|&nbsp;&nbsp;
            <strong>Tuesday:</strong> 10 AM – 7 PM (Virtual only) &nbsp;&nbsp;|&nbsp;&nbsp;
            <strong>Thursday:</strong> 10 AM – 7 PM
          </p>
          <p style={{ marginTop: 12, color: "#000", fontSize: "0.95rem", display: "flex", alignItems: "center", justifyContent: "center", gap: 16 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icons.Phone /> {PHONE}</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icons.Mail /> {EMAIL}</span>
          </p>
        </div>
      </section>

      <section className="section">
        <CTA />
      </section>

      {/* FAQ */}
      <section className="section-full section-cream">
        <div className="section" style={{ paddingTop: 0, paddingBottom: 0 }}>
          <FAQ />
        </div>
      </section>
    </div>
  );
}


// ─── PAGE: CHILD PSYCHOLOGY ─────────────────────────────────
function ChildPsychologyPage() {
  useSeo({
    title: "Child & Adolescent Psychology",
    description: "Compassionate child and adolescent psychology in Manhattan: neuropsychological testing, evaluations, and therapy for children ages 2–18 with Dr. Eugenia Steingold.",
    path: "/child-psychology",
  });
  return (
    <div>
      <section className="page-hero">
        <p className="section-label">Child Psychology</p>
        <h1>NYC Child Psychology</h1>
        <p>Specialized care and integrated treatment for your child's unique developmental journey.</p>
      </section>

      <section className="section" style={{ fontSize: "1.125rem" }}>
        <div className="grid-2">
          <div className="fade-in">
            <p style={{ color: "#000", lineHeight: 1.8, marginBottom: 16, fontSize: "inherit" }}>
              I treat Autism Spectrum Disorder, Attention Deficit Disorders, Depression, Anxiety, and phobias, as well as various behavioral and emotional problems. I also conduct neuropsychological testing and provide therapy and training for parents. I am fully bilingual in English and Russian.
            </p>
            <p style={{ color: "#000", lineHeight: 1.8, marginBottom: 16, fontSize: "inherit" }}>
              There is hardly anything more difficult or scary for a parent than realizing that your child struggles with their ability to learn, develop, and function. As a New York State licensed psychologist and a Board-Certified psychotherapist specializing in child development, I am here to help.
            </p>
          </div>
          <div className="fade-in fade-in-d2">
            <div className="meet-photo" style={{ aspectRatio: "4/3" }}>
              <Img url="/media/child-1.webp" />
            </div>
          </div>
        </div>
      </section>

      <section className="section-full section-olive-light" style={{ fontSize: "1.125rem", padding: "62px 24px" }}>
        <div className="section" style={{ paddingTop: 0, paddingBottom: 0 }}>
          <div className="section-header" style={{ marginBottom: 30 }}>
            <h2>An Integrated Treatment Approach</h2>
          </div>
          <p className="max-w-800" style={{ margin: "0 auto", textAlign: "center", color: "#000", lineHeight: 1.8, fontSize: "inherit" }}>
            No two children are alike. My integrated treatment approach combines various methods to answer the individual needs of your child. I use the Developmental, Individual-differences, Relationship-based (DIR) Floortime model, play therapy, elements of Applied Behavior Analysis (ABA), Narrative therapy, and Cognitive Behavioral therapy, tailored to your child's unique profile.
          </p>
        </div>
      </section>

      <section className="section" style={{ fontSize: "1.125rem" }}>
        <div className="max-w-800">
          <Expandable title="Anxiety, Depression, Bipolar Disorder" className="child-expandable" defaultOpen>
            <p className="child-quote">"Will I ever get my child back?"</p>
            <div className="child-question-box">
              <p>Is your child sleepy, lethargic, apathetic most of the time?</p>
              <p>Does your child have trouble falling asleep, and stay up very late?</p>
              <p>Does your child eat uncontrollably? Or, on the contrary, does she barely eat anything at all?</p>
              <p>Do you feel that he has lost interest in his usual activities and hobbies?</p>
              <p>Is he neglecting his responsibilities?</p>
              <p>Do you feel you can no longer communicate and reason with him?</p>
            </div>
            <p>
              If you answered “yes” to some of the questions above, your child may be suffering from a mood disorder. Depression can be triggered by a significant event, but it can also develop gradually. Depending on your child's age, processing and interactive style, I will use a combination of Play Therapy, Narrative Therapy (we will write a book about your child together), and Cognitive Behavioral Therapy. As always, individual temperament, developmental needs, and unique family dynamics will be deciding factors in designing the individual treatment plan for your child.
            </p>
          </Expandable>

          <Expandable title="Attention-Deficit/Hyperactivity Disorder" className="child-expandable">
            <p className="child-quote">"Why is my child acting so crazy?"</p>
            <div className="child-question-box">
              <p>Is your toddler running from one toy to another without giving herself enough time to explore the toy meaningfully?</p>
              <p>Is your child easily distracted? Does she interrupt her activities frequently?</p>
              <p>Is she forgetful about her daily tasks and responsibilities?</p>
              <p>Is he hypersensitive and impulsive? Does she have difficulties with self-control?</p>
              <p>Does he lie excessively?</p>
            </div>
            <p>
              ADHD is only a label and your child's specific characteristics will differ dramatically from those of another child with ADHD. In general a child with ADHD struggles with her daily tasks (frequently, despite being very bright), due to her difficulties with sustaining concentration. Many children with ADHD can concentrate fully for a short period of time but then easily become overwhelmed and have to interrupt their tasks.
            </p>
            <p>
              During the course of therapy, I will teach you and your child (depending on the age) a set of Relaxation Techniques which will help your child increase his or her ability to concentrate. I will also work with your child's school to create the optimal learning environment for him or her.
            </p>
          </Expandable>

          <Expandable title="Autism Spectrum Disorder (ASD)" className="child-expandable">
            <p className="child-quote">"Can my child be helped?"</p>
            <div className="child-question-box">
              <p>Does your child struggle making friends?</p>
              <p>Is she being unreasonable and difficult to understand?</p>
              <p>Does he throw heavy tantrums or become suddenly aggressive?</p>
              <p>Does she struggle with language, attention, socialization and relatedness?</p>
            </div>
            <p>
              Devastated to say the least. That is how most parents feel when they begin to suspect that their child might have autism spectrum disorder. Those labels usually mean that some fundamental ability to relate and communicate is affected. In addition, your child's ability to speak and understand language may be affected.
            </p>
            <p>
              In my practice, I help the parents move beyond the specific label, and develop an individual profile for each child. Once the assessment is complete and the individual profile is developed, a specific treatment course is designed. For example, the DIR Floortime model will be used for a relatable child with language difficulties. An Applied Behavioral Analysis (ABA) is highly effective for teaching specific skills. As your child makes progress, Play Therapy and specific Cognitive Learning tasks will be added. With the correctly designed intervention, your child can thrive.
            </p>
          </Expandable>

          <Expandable title="Psychological Trauma and Loss, Grief and Bereavement" className="child-expandable">
            <p className="child-quote">"How can my child heal from loss?"</p>
            <p>
              Children, even the youngest ones, are affected and wounded by loss with the same profoundness and acuteness. Time seems to help reduce those feelings, but frequently a word or picture triggers them and makes them resurface again. Children also feel confused and scared due to their limited understanding of what is going on. Often they cannot fully communicate their feelings which increases their confusion and loneliness.
            </p>
            <p>
              Children re-live their losses year after year, every time on a new level and scale. They can be helped to re-live their trauma with increased understanding and acceptance. Whether your child is affected by a recent loss or still dwells on the trauma that occurred years ago, psychotherapy can help her accept what happened and cope with an extremely difficult and painful situation.
            </p>
            <span style={{ display: "block", width: "100%", height: 220, borderRadius: 12, overflow: "hidden", marginTop: 10 }}>
              <Img url="/media/memorial.png" />
            </span>
            <p style={{ textAlign: "center", marginTop: 10, fontStyle: "italic", color: "var(--text-light)", fontSize: "0.95rem" }}>
              No matter how many years pass, every New Yorker has a sharp feeling of loss at the reminder of September 11, 2001.
            </p>
          </Expandable>
        </div>
      </section>

      {/* Child testimonials */}
      <section className="section-full section-blue" style={{ padding: "60px 24px" }}>
        <div className="section" style={{ paddingTop: 0, paddingBottom: 0 }}>
          <div className="testimonials-grid">
            {[TESTIMONIALS_STUBS[1], TESTIMONIALS_STUBS[0], TESTIMONIALS_FULL[6]].map((t, i) => <TestimonialCard key={i} t={t} />)}
          </div>
        </div>
      </section>
    </div>
  );
}


// ─── PAGE: TESTIMONIALS ─────────────────────────────────────
function TestimonialsPage() {
  useSeo({
    title: "Patient Testimonials & Reviews",
    description: "Read testimonials from patients and parents about their experience with Dr. Eugenia Steingold, a trusted clinical psychologist in Manhattan.",
    path: "/testimonials",
  });
  return (
    <div>
      <section className="page-hero">
        <p className="section-label">Testimonials</p>
        <h1>What My Patients Say</h1>
      </section>

      <section className="section">
        <div className="testimonials-grid">
          {[...TESTIMONIALS_FULL, ...TESTIMONIALS_STUBS].map((t, i) => <TestimonialCard key={i} t={t} />)}
        </div>
      </section>

      <section className="section">
        <CTA text="Ready to Write Your Own Story?" />
      </section>
    </div>
  );
}


// ─── PAGE: BLOG ─────────────────────────────────────────────
function BlogPage() {
  const { go, linkProps } = useRoute();
  const [blogs, setBlogs] = useState(null);
  const [links, setLinks] = useState([]);
  useSeo({
    title: "Blog & Media",
    description: "Insights on psychology, personal growth, parenting, and mental health from Dr. Eugenia Steingold, a licensed clinical psychologist in Manhattan.",
    path: "/blog",
  });

  useEffect(() => {
    let active = true;
    Promise.all([api.getPublicBlogs(), api.getPublicLinks()])
      .then(([blogsResp, linksResp]) => {
        if (!active) return;
        setBlogs(Array.isArray(blogsResp.blogs) ? blogsResp.blogs : []);
        if (Array.isArray(linksResp.links)) setLinks(linksResp.links);
      })
      .catch(() => { if (active) setBlogs([]); });
    return () => { active = false; };
  }, []);

  return (
    <div>
      <section className="page-hero">
        <p className="section-label">Blog & Media</p>
        <h1>Blog & Media</h1>
        <p>Insights on psychology, personal growth, and parenting from Dr. Eugenia Steingold.</p>
      </section>

      <section className="section">
        {/* External Links */}
        {links.length > 0 && (
          <div className="max-w-800 mb-48">
            <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.2rem", marginBottom: 16, color: "#000" }}>External Resources</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {links.map(l => (
                <a key={l.id} href={l.url} target="_blank" rel="noopener noreferrer"
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", borderRadius: 10, border: "1px solid rgba(0,0,0,0.08)", background: "#fff", textDecoration: "none", color: "#000", fontSize: "0.92rem", transition: "all 0.2s" }}>
                  <span style={{ color: "var(--olive)" }}><Icons.Link /></span>
                  <span style={{ fontWeight: 600 }}>{l.title}</span>
                  {l.desc && <span style={{ color: "var(--text-light)", fontSize: "0.85rem" }}>— {l.desc}</span>}
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="blog-grid">
          {blogs !== null && blogs.map((b) => (
            <a key={b.id} className="blog-card" style={{ color: "inherit", textDecoration: "none", display: "block" }} {...linkProps(`/blog/${b.slug}`)}>
              <div className="blog-card-img" style={{ position: "relative", overflow: "hidden" }}>
                {getBlogImageUrl(b)
                  ? <img src={getBlogImageUrl(b)} alt={b.title} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <ImgPlaceholder text={b.title} />}
              </div>
              <div className="blog-card-body">
                <div className="blog-card-date">{b.date}</div>
                <h3>{b.title}</h3>
                <p>{b.desc}</p>
              </div>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}

function BlogPostPage({ slug }) {
  const { go } = useRoute();
  const [post, setPost] = useState(undefined);

  useEffect(() => {
    let active = true;
    setPost(undefined);
    api.getPublicBlogBySlug(slug)
      .then((resp) => {
        if (active) setPost(resp.blog || null);
      })
      .catch(() => { if (active) setPost(null); });
    return () => { active = false; };
  }, [slug]);

  const ogImage = (() => {
    if (!post) return OG_IMAGE;
    const u = getBlogImageUrl(post);
    if (!u) return OG_IMAGE;
    return u.startsWith("/") ? `${SITE_URL}${u}` : u;
  })();
  useSeo({
    title: post ? post.title : "Blog",
    description: post ? (post.desc || DEFAULT_DESC) : DEFAULT_DESC,
    path: `/blog/${slug}`,
    image: ogImage,
    type: "article",
  });
  const articleLd = post ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.desc || undefined,
    image: ogImage,
    datePublished: (() => { const d = new Date(post.date); return Number.isNaN(d.getTime()) ? undefined : d.toISOString(); })(),
    author: { "@type": "Person", name: "Dr. Eugenia Steingold" },
    publisher: { "@type": "Organization", name: SITE_NAME, logo: { "@type": "ImageObject", url: OG_IMAGE } },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${slug}` },
  } : null;
  useJsonLd("ld-article", articleLd);

  if (post === undefined) return (
    <div>
      <section className="page-hero"><h1>Loading...</h1></section>
    </div>
  );

  if (!post) return (
    <div>
      <section className="page-hero"><h1>Post Not Found</h1></section>
      <section className="section text-center"><button className="btn-outline" onClick={() => go("/blog")}>← Back to Blog</button></section>
    </div>
  );

  const getYouTubeId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/);
    return m ? m[1] : null;
  };
  const ytId = getYouTubeId(post.media);

  return (
    <div>
      <section className="page-hero" style={{ padding: "118px 24px 34px" }}>
        <p className="section-label">{post.date}</p>
        <h1>{post.title}</h1>
      </section>
      <section className="section max-w-800 blog-post-section" style={{ paddingTop: 20 }}>
        <button className="btn-outline blog-back-btn" onClick={() => go("/blog")}>← Back to Blog</button>
        {getBlogImageUrl(post) && (
          <div style={{ borderRadius: 16, overflow: "hidden", marginBottom: 32, textAlign: "center" }}>
            <img src={getBlogImageUrl(post)} alt={post.title} style={{ width: "auto", maxWidth: "100%", height: 500, objectFit:"cover", display: "block", margin: "0 auto" }} />
          </div>
        )}
        <div
          className="blog-content"
          dangerouslySetInnerHTML={{ __html: post.contentHtml || textToHtml(post.content || "") }}
        />
        {ytId && (
          <div style={{ marginTop: 32, borderRadius: 16, overflow: "hidden", aspectRatio: "16/9" }}>
            <iframe width="100%" height="100%" src={`https://www.youtube.com/embed/${ytId}`}
              frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen style={{ border: 0 }} />
          </div>
        )}
      </section>
    </div>
  );
}


// ─── PAGE: CONTACT ──────────────────────────────────────────
function ContactPage() {
  useSeo({
    title: "Request an Appointment | Contact",
    description: "Request an appointment with Dr. Eugenia Steingold in Manhattan. Call (347) 779-2473 or complete the contact form. In-person and virtual (Zoom) sessions available.",
    path: "/contact",
  });
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", age: "", concern: "", referred: "", virtual: false });
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleSubmit = async () => {
    if (!form.firstName || !form.lastName || !form.email || !form.phone || !form.age || !form.concern) {
      alert("Please fill in all required fields.");
      return;
    }
    setSubmitError("");
    try {
      await api.createSubmission(form);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err?.message || "Unable to submit. Please try again.");
    }
  };

  return (
    <div>
      <section className="page-hero">
        <p className="section-label">Request Appointment</p>
        <h1>Contact Me</h1>
        <p>I look forward to hearing from you. Please use the form below for appointment requests or the details provided for other inquiries.</p>
      </section>

      <section className="section">
        <div className="grid-2">
          {/* Contact info */}
          <div>
            <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.3rem", color: "#000", marginBottom: 20 }}>Contact Information</h3>
            <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 12, color: "#000" }}>
              <span style={{ color: "var(--olive)" }}><Icons.Phone /></span> <a href={`tel:${PHONE.replace(/\D/g, '')}`} style={{ color: "#000", textDecoration: "none", cursor: "pointer" }}>{PHONE}</a>
            </div>
            <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 12, color: "#000" }}>
              <span style={{ color: "var(--olive)" }}><Icons.Mail /></span> <a href={`mailto:${EMAIL}`} style={{ color: "#000", textDecoration: "none", cursor: "pointer" }}>{EMAIL}</a>
            </div>
            <div style={{ marginBottom: 24, display: "flex", alignItems: "center", gap: 12, color: "#000" }}>
              <span style={{ color: "var(--olive)" }}><Icons.MapPin /></span>
              <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" style={{ color: "#000", textDecoration: "none" }}>{ADDRESS}</a>
            </div>

            <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.15rem", color: "#000", marginBottom: 12, marginTop: 32 }}>Office Hours</h3>
            <p style={{ color: "#000", marginBottom: 6 }}><strong>Mon & Thu:</strong> 10:00 AM - 7:00 PM</p>
            <p style={{ color: "#000" }}><strong>Tuesday:</strong> 10:00 AM - 7:00 PM (Virtual only)</p>
          </div>

          {/* Form */}
          <div>
            <div style={{ background: "var(--cream)", padding: 36, borderRadius: 20 }}>
              <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.3rem", color: "#000", marginBottom: 8 }}>Request an Appointment</h3>
              <p style={{ fontSize: "0.9rem", color: "#000", marginBottom: 24, lineHeight: 1.6 }}>
                First-time patients, please complete this secure form. We will respond to you within three business days.
              </p>

              {submitted ? (
                <div style={{ textAlign: "center", padding: 40 }}>
                  <div style={{ marginBottom: 16 }}><Icons.CheckCircle /></div>
                  <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.3rem", color: "#000", marginBottom: 12 }}>Thank You</h3>
                  <p style={{ color: "#000" }}>Thank you for submitting a request. We will do our best to respond within three business days.</p>
                </div>
              ) : (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Patient's First Name</label>
                      <input className="form-input" placeholder="First Name" value={form.firstName} onChange={e => setForm({...form, firstName: e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label>Patient's Last Name</label>
                      <input className="form-input" placeholder="Last Name" value={form.lastName} onChange={e => setForm({...form, lastName: e.target.value})} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Your Email</label>
                    <input className="form-input" type="email" placeholder="name@example.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Your Phone Number</label>
                      <input className="form-input" type="tel" placeholder="(555) 555-5555" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label>Patient's Age</label>
                      <input className="form-input" placeholder="e.g., 8 or Adult" value={form.age} onChange={e => setForm({...form, age: e.target.value})} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Briefly Describe Your Concern</label>
                    <textarea className="form-input" placeholder="Please provide a brief, non-confidential summary of your concerns." maxLength={3000} value={form.concern} onChange={e => setForm({...form, concern: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>Referred by</label>
                    <input className="form-input" placeholder="Name of referrer" value={form.referred} onChange={e => setForm({...form, referred: e.target.value})} />
                  </div>
                  <div className="checkbox-row">
                    <label onClick={() => setForm({...form, virtual: !form.virtual})}>Do you prefer a virtual appointment?</label>
                    <div className={`custom-checkbox ${form.virtual ? "checked" : ""}`} onClick={() => setForm({...form, virtual: !form.virtual})} />
                  </div>
                  <button className="btn-primary" style={{ width: "100%", justifyContent: "center", marginTop: 8 }} onClick={handleSubmit}>
                    Submit Your Request
                  </button>
                  {submitError && <p style={{ color: "#c00", fontSize: "0.85rem", marginTop: 10 }}>{submitError}</p>}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 3 featured testimonials AFTER the form */}
      <section className="section-full section-blue" style={{ padding: "60px 24px" }}>
        <div className="section" style={{ paddingTop: 0, paddingBottom: 0 }}>
          <div className="section-header" style={{ marginBottom: 32 }}>
            <h2>What Others Are Saying</h2>
          </div>
          <div className="testimonials-grid">
            {TESTIMONIALS_FULL.slice(0, 3).map((t, i) => <TestimonialCard key={i} t={t} />)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-full section-cream">
        <div className="section" style={{ paddingTop: 0, paddingBottom: 0 }}>
          <FAQ id="faq" />
        </div>
      </section>
    </div>
  );
}


// ─── PAGE: ADMIN ────────────────────────────────────────────
function AdminPage() {
  useSeo({ title: "Admin", path: "/admin", noindex: true });
  const { loggedIn, login, logout, admin, session, refreshSession } = useAuth();
  const { page, go } = useRoute();
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [loginCode, setLoginCode] = useState("");
  const [loginChallengeId, setLoginChallengeId] = useState("");
  const [authStep, setAuthStep] = useState("login");
  const [recoveryIdentifier, setRecoveryIdentifier] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [recoveryResetToken, setRecoveryResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [recoveryMessage, setRecoveryMessage] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("submissions");
  const [subs, setSubs] = useState([]);
  const [archivedSubs, setArchivedSubs] = useState([]);
  // The detail view loads its own copy so the read is recorded in the audit log.
  const [subDetail, setSubDetail] = useState(null);
  const [subDetailLoading, setSubDetailLoading] = useState(false);
  const [subDetailErr, setSubDetailErr] = useState("");
  const [blogs, setBlogs] = useState([]);
  const [links, setLinks] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [editingAdminId, setEditingAdminId] = useState(null);
  const [adminFormMsg, setAdminFormMsg] = useState("");
  const [adminForm, setAdminForm] = useState({
    username: "",
    email: "",
    password: "",
    role: "USER_ADMIN",
    receivesEmails: true,
    authCodeRequired: true,
    isActive: true,
  });
  const [emailCopied, setEmailCopied] = useState(false);
  const [editing, setEditing] = useState(null);
  const [blogForm, setBlogForm] = useState({
    title: "", desc: "", contentHtml: "", date: "", media: "",
    imgSource: "upload", imgUrl: "", imgAssetId: ""
  });
  const [myEmail, setMyEmail] = useState("");
  const [myEmailMsg, setMyEmailMsg] = useState("");
  const [linkForm, setLinkForm] = useState({ title: "", url: "", desc: "" });
  const [editingLink, setEditingLink] = useState(null);
  // { action, title, body, confirmLabel, confirmColor }
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const editorRef = useRef(null);
  const contentHtmlRef = useRef("");
  const [linkPopover, setLinkPopover] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPages, setAuditPages] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [subsLoading, setSubsLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(false);
  const [bodyCopied, setBodyCopied] = useState(false);
  const [copiedHashId, setCopiedHashId] = useState(null);

  useEffect(() => {
    if (editing != null && editorRef.current) {
      editorRef.current.innerHTML = blogForm.contentHtml;
      contentHtmlRef.current = blogForm.contentHtml;
    }
  }, [editing]);

  const submissionId = page.startsWith("/admin/submission/") ? page.split("/admin/submission/")[1] : null;

  const loadDashboardData = useCallback(async () => {
    if (!loggedIn) return;
    setSubsLoading(true);
    try {
      const isMaster = admin?.role === "MASTER_ADMIN";
      const calls = [
        api.getSubmissions(),
        api.getArchivedSubmissions(),
        api.getAdminBlogs(),
        api.getAdminLinks(),
        ...(isMaster ? [api.getAdmins()] : []),
      ];
      const [subResult, archivedResult, blogResult, linkResult, adminResult] = await Promise.allSettled(calls);

      if (subResult.status === "fulfilled") {
        setSubs(subResult.value?.submissions || []);
      } else if (subResult.reason?.status === 401) {
        await logout();
        return;
      }

      if (archivedResult.status === "fulfilled") setArchivedSubs(archivedResult.value?.submissions || []);
      if (blogResult.status === "fulfilled") setBlogs(blogResult.value?.blogs || []);
      if (linkResult.status === "fulfilled") setLinks(linkResult.value?.links || []);
      if (adminResult?.status === "fulfilled" && adminResult.value?.admins) {
        setAdmins(adminResult.value.admins);
      }
    } finally {
      setSubsLoading(false);
    }
  }, [loggedIn, admin?.role, logout]);

  useEffect(() => {
    if (loggedIn) {
      setMyEmail(admin?.email || "");
      loadDashboardData();
    }
  }, [loggedIn, admin?.email, loadDashboardData]);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token");
    if (token) {
      setRecoveryResetToken(token);
      setAuthStep("recovery-reset");
    }
  }, [page]);

  // Load the single submission from the server rather than reusing the list
  // payload: the by-id endpoint is what writes the SUBMISSION_VIEW audit entry,
  // so every open of this page is attributed to the admin who opened it.
  const viewedSubmissionRef = useRef(null);
  useEffect(() => {
    if (!loggedIn || !submissionId) return;
    // StrictMode double-invokes effects in development. Without this guard each
    // page open would record two identical view entries.
    if (viewedSubmissionRef.current === submissionId) return;
    viewedSubmissionRef.current = submissionId;

    setSubDetailLoading(true);
    setSubDetailErr("");
    api.getSubmissionById(submissionId)
      .then(resp => setSubDetail(resp.submission || null))
      .catch(e => {
        // Allow a retry on the next render if the read failed.
        viewedSubmissionRef.current = null;
        setSubDetail(null);
        setSubDetailErr(e?.status === 404 ? "Submission not found." : (e?.message || "Unable to load submission."));
      })
      .finally(() => setSubDetailLoading(false));
  }, [loggedIn, submissionId]);

  const loadAuditLogs = useCallback(async (pg = 1) => {
    setAuditLoading(true);
    try {
      const resp = await api.getAuditLogs(pg);
      setAuditLogs(resp.logs || []);
      setAuditPage(resp.page || 1);
      setAuditPages(resp.pages || 1);
      setAuditTotal(resp.total || 0);
    } catch {} finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    if (loggedIn && admin?.role === "MASTER_ADMIN" && tab === "audit") {
      loadAuditLogs(auditPage);
    }
  }, [loggedIn, admin?.role, tab, auditPage, loadAuditLogs]);

  const [countdown, setCountdown] = useState("--:--");

  useEffect(() => {
    if (!session?.expiresAt) return;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((new Date(session.expiresAt) - Date.now()) / 1000));
      if (remaining === 0) {
        window.location.reload();
        return;
      }
      const m = Math.floor(remaining / 60);
      const s = remaining % 60;
      setCountdown(`${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [session?.expiresAt]);

  const exportCsv = () => {
    const headers = ["First Name", "Last Name", "Email", "Phone", "Age", "Virtual", "Concern", "Referred By", "Submitted At", "Submitter IP"];
    const rows = subs.map(s => [
      s.firstName, s.lastName, s.email, s.phone, s.age,
      s.virtual ? "Yes" : "No", s.concern, s.referred || "", s.date, s.submitterIp || "N/A"
    ]);
    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell || "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const _now = new Date();
    const _estParts = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(_now);
    const _ep = Object.fromEntries(_estParts.map(x => [x.type, x.value]));
    a.download = `submissions-${_ep.year}-${_ep.month}-${_ep.day}_${_ep.hour}-${_ep.minute}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const buildEmailBody = (sub) => {
    const dateStr = sub.submittedAt
      ? new Date(sub.submittedAt).toLocaleString("en-US", {
          month: "long", day: "numeric", year: "numeric",
          hour: "2-digit", minute: "2-digit",
          timeZone: "America/New_York",
        })
      : sub.date;
    return [
      `Hello ${sub.firstName},`,
      ``,
      ``,
      ``,
      `Submission: ${dateStr}`,
      `${sub.firstName} ${sub.lastName}`,
      `Age: ${sub.age}`,
      `Concern: ${sub.concern}`,
      sub.phone,
      sub.email,
      `Virtual Preference: ${sub.virtual ? "Yes" : "No"}`,
    ].join("\n");
  };

  const buildEmailSubject = (sub) => {
    const lastInitial = sub.lastName ? sub.lastName.charAt(0).toUpperCase() + "." : "";
    return `${sub.firstName} ${lastInitial} Steingold Psychology Inquiry`;
  };

  const copyEmailBody = async (sub) => {
    const body = buildEmailBody(sub);
    try {
      await navigator.clipboard.writeText(body);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = body;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setBodyCopied(true);
    setTimeout(() => setBodyCopied(false), 1600);
  };

  const composeEmail = (sub) => {
    const subject = buildEmailSubject(sub);
    const body = buildEmailBody(sub);
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_blank");
  };

  const copyIpHash = async (id, hash) => {
    if (!hash) return;
    try {
      await navigator.clipboard.writeText(hash);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = hash;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopiedHashId(id);
    setTimeout(() => setCopiedHashId(null), 1600);
  };

  // Renders the audit metadata blob compactly: the failure reason for login
  // attempts, the attempted identifier for unknown-account misses, etc.
  const formatAuditMeta = (meta) => {
    const entries = Object.entries(meta || {}).filter(([k, v]) => k !== "submissionId" && v !== null && v !== "");
    if (entries.length === 0) return "";
    return entries.map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`).join(" · ");
  };

  const formatAuditDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      timeZone: "America/New_York", timeZoneName: "short",
    });
  };

  const handleLogin = async () => {
    setErr("");
    setBusy(true);
    try {
      const resp = await api.authLoginStart({ identifier: user, password: pass });
      if (resp.requiresCode) {
        setLoginChallengeId(resp.challengeId);
        setAuthStep("login-code");
      } else {
        login(resp.admin || null);
        await refreshSession();
        go("/admin");
      }
    } catch (e) {
      setErr(e?.message || "Unable to log in.");
    } finally {
      setBusy(false);
    }
  };

  const verifyLoginCode = async () => {
    setErr("");
    setBusy(true);
    try {
      const resp = await api.authLoginVerifyCode({ challengeId: loginChallengeId, code: loginCode });
      login(resp.admin || null);
      await refreshSession();
      go("/admin");
    } catch (e) {
      setErr(e?.message || "Invalid code.");
    } finally {
      setBusy(false);
    }
  };

  const startRecovery = async () => {
    setErr("");
    setRecoveryMessage("");
    setBusy(true);
    try {
      const resp = await api.authRecoveryStart({ identifier: recoveryIdentifier });
      // The server deliberately answers the same way for unknown identifiers so
      // the endpoint cannot be used to enumerate accounts. The wording therefore
      // has to be conditional rather than claiming an email was definitely sent.
      if (resp?.requiresCode === false) {
        setRecoveryMessage("If that username or email matches an account, a reset link is on its way. Check your inbox.");
        setAuthStep("login");
      } else {
        setRecoveryMessage("If that username or email matches an account, a recovery code has been sent. Enter it below.");
        setAuthStep("recovery-code");
      }
    } catch (e) {
      setErr(e?.message || "Unable to start recovery.");
    } finally {
      setBusy(false);
    }
  };

  const verifyRecoveryCode = async () => {
    setErr("");
    setBusy(true);
    try {
      const resp = await api.authRecoveryVerifyCode({ identifier: recoveryIdentifier, code: recoveryCode });
      setRecoveryResetToken(resp.resetToken || "");
      setAuthStep("recovery-reset");
    } catch (e) {
      setErr(e?.message || "Invalid recovery code.");
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async () => {
    setErr("");
    setBusy(true);
    try {
      await api.authRecoveryResetPassword({ resetToken: recoveryResetToken, newPassword });
      // Strip the now-spent token from the address bar. Leaving it there means a
      // reload (or any route change) drops the user back on the reset form with a
      // token the server has already consumed, so their next attempt fails with
      // "Reset token is invalid or expired" for no visible reason.
      if (new URLSearchParams(window.location.search).get("token")) {
        window.history.replaceState({}, "", "/admin");
      }
      setRecoveryMessage("Password updated. Please log in.");
      setAuthStep("login");
      setNewPassword("");
      setRecoveryResetToken("");
      setRecoveryCode("");
      setPass("");
    } catch (e) {
      setErr(e?.message || "Unable to reset password.");
    } finally {
      setBusy(false);
    }
  };

  // ─── Submissions ───
  // Submissions are never deleted. Archiving moves them into the Archived tab,
  // where they stay viewable and can be restored.
  const archiveSub = async (id) => {
    await api.archiveSubmission(id);
    setDeleteConfirm(null);
    await loadDashboardData();
    if (submissionId === id) go("/admin");
  };

  const unarchiveSub = async (id) => {
    await api.unarchiveSubmission(id);
    setDeleteConfirm(null);
    // Update the open detail view in place rather than refetching it, which
    // would record a second view of the same submission in the audit log.
    setSubDetail(prev => (prev && prev.id === id ? { ...prev, archived: false, archivedAt: null } : prev));
    await loadDashboardData();
  };

  // ─── Blog management ───
  const saveBlog = async () => {
    const payload = {
      title: blogForm.title,
      desc: blogForm.desc,
      media: blogForm.media,
      contentHtml: contentHtmlRef.current || "",
      imgSource: blogForm.imgSource,
      imgUrl: blogForm.imgSource === "url" ? blogForm.imgUrl : "",
      imgAssetId: blogForm.imgSource === "upload" ? blogForm.imgAssetId : "",
      isPublished: true,
    };
    if (editing === "new") {
      const created = await api.createBlog(payload);
      setBlogs(prev => [created.blog, ...prev]);
    } else {
      const updated = await api.updateBlog(editing, payload);
      setBlogs(prev => prev.map(b => b.id === editing ? updated.blog : b));
    }
    setEditing(null);
  };

  const deleteBlog = async (id) => {
    await api.deleteBlog(id);
    setBlogs(prev => prev.filter(b => b.id !== id));
    setDeleteConfirm(null);
  };

  const startEditBlog = (b) => {
    setBlogForm({
      title: b.title || "",
      desc: b.desc || "",
      contentHtml: b.contentHtml || textToHtml(b.content || ""),
      date: b.date || "",
      media: b.media || "",
      imgSource: b.imgSource || (b.imgUrl ? "url" : b.imgAssetId ? "upload" : "upload"),
      imgUrl: b.imgUrl || (b.img && !String(b.img).startsWith("data:") ? b.img : ""),
      imgAssetId: b.imgAssetId || "",
    });
    setEditing(b.id);
  };

  const startNewBlog = () => {
    setBlogForm({ title: "", desc: "", contentHtml: "", date: "", media: "", imgSource: "upload", imgUrl: "", imgAssetId: "" });
    setEditing("new");
  };

  // ─── Links management ───
  const saveLink = async () => {
    if (editingLink === "new") {
      const created = await api.createLink(linkForm);
      setLinks(prev => [...prev, created.link]);
    } else {
      const updated = await api.updateLink(editingLink, linkForm);
      setLinks(prev => prev.map(l => l.id === editingLink ? updated.link : l));
    }
    setEditingLink(null);
    setLinkForm({ title: "", url: "", desc: "" });
  };

  const deleteLink = async (id) => {
    await api.deleteLink(id);
    setLinks(prev => prev.filter(l => l.id !== id));
    setDeleteConfirm(null);
  };

  // Rich text helpers
  const applyFormat = (cmd, val = null) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(cmd, false, val);
    contentHtmlRef.current = editorRef.current.innerHTML;
  };

  const insertLink = () => {
    if (!editorRef.current) return;
    const sel = window.getSelection();
    if (!sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    const selectedText = range.toString();

    // Create the <a> element immediately so we have a reference to position the popover
    const a = document.createElement("a");
    a.href = "#";
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.textContent = selectedText || "";
    a.style.color = "var(--olive)";
    a.style.textDecoration = "underline";
    range.deleteContents();
    range.insertNode(a);

    // Position popover above the link
    const rect = a.getBoundingClientRect();
    const editorRect = editorRef.current.closest(".form-group").getBoundingClientRect();
    setLinkPopover({
      el: a,
      text: selectedText || "",
      url: "",
      top: rect.top - editorRect.top - 110,
      left: Math.max(0, rect.left - editorRect.left),
    });
    contentHtmlRef.current = editorRef.current.innerHTML;
  };

  const applyLinkPopover = () => {
    if (!linkPopover || !linkPopover.el) return;
    const { el, text, url } = linkPopover;
    if (!url) { cancelLinkPopover(); return; }
    el.href = url;
    el.textContent = text || url;
    el.style.color = "";
    el.style.textDecoration = "";
    contentHtmlRef.current = editorRef.current.innerHTML;
    setLinkPopover(null);
  };

  const cancelLinkPopover = () => {
    if (linkPopover && linkPopover.el && linkPopover.el.parentNode) {
      const text = document.createTextNode(linkPopover.el.textContent);
      linkPopover.el.parentNode.replaceChild(text, linkPopover.el);
      contentHtmlRef.current = editorRef.current.innerHTML;
    }
    setLinkPopover(null);
  };

  // Handle image upload for blog
  const handleImgUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const resp = await api.uploadMedia(file);
    setBlogForm(prev => ({ ...prev, imgSource: "upload", imgAssetId: resp.asset.id, imgUrl: "" }));
  };

  const openSubmissionInNewTab = (id) => {
    const url = `${window.location.origin}/admin/submission/${id}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      setEmailCopied(true);
      window.setTimeout(() => setEmailCopied(false), 1600);
    } catch {
      const input = document.createElement("input");
      input.value = email;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setEmailCopied(true);
      window.setTimeout(() => setEmailCopied(false), 1600);
    }
  };

  const saveMyEmail = async () => {
    setMyEmailMsg("");
    try {
      await api.updateMyEmail(myEmail);
      await refreshSession();
      setMyEmailMsg("Email updated.");
    } catch (e) {
      setMyEmailMsg(e?.message || "Unable to update email.");
    }
  };

  const saveAdminUser = async () => {
    // Without this the server's validation errors (password too short, username
    // already taken) rejected silently and the form simply appeared to do
    // nothing, leaving the admin believing a password had been set when it had not.
    setAdminFormMsg("");
    try {
      if (editingAdminId === "new") {
        const created = await api.createAdmin(adminForm);
        setAdmins(prev => [created.admin, ...prev]);
      } else {
        const updatePayload = { ...adminForm };
        if (!updatePayload.password) delete updatePayload.password;
        const updated = await api.updateAdmin(editingAdminId, updatePayload);
        setAdmins(prev => prev.map(a => a.id === editingAdminId ? updated.admin : a));
      }
    } catch (e) {
      setAdminFormMsg(e?.message || "Unable to save this admin user.");
      return;
    }
    setEditingAdminId(null);
    setAdminForm({
      username: "",
      email: "",
      password: "",
      role: "USER_ADMIN",
      receivesEmails: true,
      authCodeRequired: true,
      isActive: true,
    });
  };

  const deleteAdminUser = async (id) => {
    setAdminFormMsg("");
    try {
      await api.deleteAdmin(id);
      setAdmins(prev => prev.map(a => a.id === id ? { ...a, isActive: false } : a));
    } catch (e) {
      setAdminFormMsg(e?.message || "Unable to disable this admin user.");
    }
    setDeleteConfirm(null);
  };

  // ─── Login screen ───
  if (!loggedIn) {
    return (
      <div className="admin-login">
        <div className="admin-box">
          <div className="text-center" style={{ marginBottom: 20 }}><Icons.Lock /></div>
          <h2>Admin Access</h2>
          <p style={{ fontSize: "0.82rem", color: "var(--text-light)", textAlign: "center", marginBottom: 24 }}>
            HIPAA-compliant secure dashboard. All data is encrypted and access-controlled.
          </p>
          {authStep === "login" && (
            <>
              <div className="form-group">
                <label>Username or Email</label>
                <input className="form-input" value={user} onChange={e => { setUser(e.target.value); setErr(""); }} autoComplete="off" />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input className="form-input" type="password" value={pass} onChange={e => { setPass(e.target.value); setErr(""); }}
                  onKeyDown={e => e.key === "Enter" && handleLogin()} autoComplete="off" />
              </div>
            </>
          )}
          {authStep === "login-code" && (
            <div className="form-group">
              <label>Authentication Code</label>
              <input className="form-input" value={loginCode} onChange={e => { setLoginCode(e.target.value); setErr(""); }} autoComplete="off"
                onKeyDown={e => e.key === "Enter" && verifyLoginCode()} />
            </div>
          )}
          {authStep === "recovery-request" && (
            <div className="form-group">
              <label>Username or Email</label>
              <input className="form-input" value={recoveryIdentifier} onChange={e => { setRecoveryIdentifier(e.target.value); setErr(""); }} autoComplete="off" />
            </div>
          )}
          {authStep === "recovery-code" && (
            <>
              <div className="form-group">
                <label>Username or Email</label>
                <input className="form-input" value={recoveryIdentifier} onChange={e => setRecoveryIdentifier(e.target.value)} autoComplete="off" />
              </div>
              <div className="form-group">
                <label>Recovery Code</label>
                <input className="form-input" value={recoveryCode} onChange={e => { setRecoveryCode(e.target.value); setErr(""); }} autoComplete="off"
                  onKeyDown={e => e.key === "Enter" && verifyRecoveryCode()} />
              </div>
            </>
          )}
          {authStep === "recovery-reset" && (
            <div className="form-group">
              <label>New Password</label>
              <input className="form-input" type="password" value={newPassword} onChange={e => { setNewPassword(e.target.value); setErr(""); }} autoComplete="off"
                placeholder="Minimum 6 characters"
                onKeyDown={e => e.key === "Enter" && resetPassword()} />
            </div>
          )}
          {err && <p style={{ color: "#c00", fontSize: "0.88rem", marginBottom: 12 }}>{err}</p>}
          {recoveryMessage && <p style={{ color: "var(--olive)", fontSize: "0.88rem", marginBottom: 12 }}>{recoveryMessage}</p>}

          {authStep === "login" && (
            <>
              <button className="btn-primary" style={{ width: "100%", justifyContent: "center" }} onClick={handleLogin} disabled={busy}>
                {busy ? "Working..." : "Log In"}
              </button>
              <button className="btn-outline" style={{ width: "100%", justifyContent: "center", marginTop: 10 }} onClick={() => setAuthStep("recovery-request")} disabled={busy}>
                Forgot Password?
              </button>
            </>
          )}
          {authStep === "login-code" && (
            <>
              <button className="btn-primary" style={{ width: "100%", justifyContent: "center" }} onClick={verifyLoginCode} disabled={busy}>
                {busy ? "Working..." : "Verify Code"}
              </button>
              <button className="btn-outline" style={{ width: "100%", justifyContent: "center", marginTop: 10 }} onClick={() => { setAuthStep("login"); setLoginCode(""); }} disabled={busy}>
                Back
              </button>
            </>
          )}
          {authStep === "recovery-request" && (
            <>
              <button className="btn-primary" style={{ width: "100%", justifyContent: "center" }} onClick={startRecovery} disabled={busy}>
                {busy ? "Working..." : "Send Recovery Code"}
              </button>
              <button className="btn-outline" style={{ width: "100%", justifyContent: "center", marginTop: 10 }} onClick={() => setAuthStep("login")} disabled={busy}>
                Back to Login
              </button>
            </>
          )}
          {authStep === "recovery-code" && (
            <>
              <button className="btn-primary" style={{ width: "100%", justifyContent: "center" }} onClick={verifyRecoveryCode} disabled={busy}>
                {busy ? "Working..." : "Verify Recovery Code"}
              </button>
              <button className="btn-outline" style={{ width: "100%", justifyContent: "center", marginTop: 10 }} onClick={() => setAuthStep("login")} disabled={busy}>
                Back to Login
              </button>
            </>
          )}
          {authStep === "recovery-reset" && (
            <>
              <button className="btn-primary" style={{ width: "100%", justifyContent: "center" }} onClick={resetPassword} disabled={busy}>
                {busy ? "Working..." : "Reset Password"}
              </button>
              <button className="btn-outline" style={{ width: "100%", justifyContent: "center", marginTop: 10 }} onClick={() => setAuthStep("login")} disabled={busy}>
                Back to Login
              </button>
            </>
          )}
          <p style={{ fontSize: "0.75rem", color: "rgba(0,0,0,0.3)", textAlign: "center", marginTop: 16 }}>
            Protected under HIPAA compliance. Unauthorized access is prohibited.
          </p>
        </div>
      </div>
    );
  }

  // ─── Delete confirmation modal ───
  const DeleteModal = () => {
    if (!deleteConfirm) return null;
    return (
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ background: "#fff", borderRadius: 20, padding: 36, maxWidth: 400, width: "90%", textAlign: "center" }}>
          <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.2rem", marginBottom: 12, color: "#000" }}>
            {deleteConfirm.title || "Confirm Deletion"}
          </h3>
          <p style={{ color: "#000", marginBottom: 24, fontSize: "0.95rem" }}>
            {deleteConfirm.body || "Are you sure? This action cannot be undone."}
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            <button className="btn-primary" style={{ background: deleteConfirm.confirmColor || "#c00" }} onClick={deleteConfirm.action}>
              {deleteConfirm.confirmLabel || "Delete"}
            </button>
            <button className="btn-outline" onClick={() => setDeleteConfirm(null)}>Cancel</button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Single submission view ───
  if (submissionId) {
    const sub = subDetail;
    return (
      <div className="admin-dash">
        <DeleteModal />
        <button className="btn-outline mb-32" onClick={() => go("/admin")}>← Back to Dashboard</button>
        {subDetailLoading ? (
          <div className="admin-card text-center" style={{ padding: 60 }}>
            <p style={{ color: "var(--text-light)" }}>Loading...</p>
          </div>
        ) : !sub ? (
          <div className="admin-card text-center" style={{ padding: 60 }}>
            <p style={{ color: "var(--text-light)" }}>{subDetailErr || "Submission not found."}</p>
          </div>
        ) : (
        <div className="admin-card">
          {sub.archived && (
            <div style={{ background: "#f6f4ee", border: "1px solid #e2ddcd", borderRadius: 10, padding: "10px 14px", marginBottom: 18, fontSize: "0.85rem", color: "var(--text-light)" }}>
              This submission is archived. Restore it to move it back to the active list.
            </div>
          )}
          <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.5rem", color: "#000", marginBottom: 8 }}>
            {sub.firstName} {sub.lastName}
          </h2>
          <p style={{ color: "var(--olive)", fontSize: "0.85rem", fontWeight: 600, marginBottom: 24 }}>{sub.date}</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div>
              <strong style={{ fontSize: "0.85rem", color: "var(--text-light)" }}>Email</strong>
              <p
                onClick={() => copyEmail(sub.email)}
                style={{ color: "#000", position: "relative", width: "fit-content", cursor: "copy", margin: 0 }}
                title="Click to copy email"
              >
                {sub.email}
                <span style={{
                position: "absolute",
                left: "calc(100% + 8px)",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "0.76rem",
                color: "var(--olive)",
                opacity: emailCopied ? 1 : 0,
                transition: "opacity 0.25s ease"
              }}>
                Copied
              </span>
              </p>
            </div>
            <div><strong style={{ fontSize: "0.85rem", color: "var(--text-light)" }}>Phone</strong><p style={{ color: "#000" }}>{sub.phone}</p></div>
            <div><strong style={{ fontSize: "0.85rem", color: "var(--text-light)" }}>Age</strong><p style={{ color: "#000" }}>{sub.age}</p></div>
            <div><strong style={{ fontSize: "0.85rem", color: "var(--text-light)" }}>Virtual Preference</strong><p style={{ color: "#000" }}>{sub.virtual ? "Yes" : "No"}</p></div>
          </div>
          <div style={{ marginTop: 20 }}>
            <strong style={{ fontSize: "0.85rem", color: "var(--text-light)" }}>Concern</strong>
            <p style={{ color: "#000", lineHeight: 1.75, marginTop: 4 }}>{sub.concern}</p>
          </div>
          {sub.referred && (
            <div style={{ marginTop: 20 }}>
              <strong style={{ fontSize: "0.85rem", color: "var(--text-light)" }}>Referred By</strong>
              <p style={{ color: "#000" }}>{sub.referred}</p>
            </div>
          )}
          <div style={{ marginTop: 20 }}>
            <strong style={{ fontSize: "0.85rem", color: "var(--text-light)" }}>Submitted From IP</strong>
            <p style={{ color: "#000" }}>{sub.submitterIp || "N/A"}</p>
          </div>
          <div style={{ marginTop: 28, padding: "20px 0", borderTop: "1px solid #eee" }}>
            <p style={{ fontSize: "0.82rem", color: "var(--text-light)", marginBottom: 12, fontWeight: 600 }}>Email Template</p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <button className="btn-outline" style={{ fontSize: "0.85rem" }} onClick={() => copyEmailBody(sub)}>
                Copy Email Body{bodyCopied ? " ✓" : ""}
              </button>
              <button className="btn-outline" style={{ fontSize: "0.85rem" }} onClick={() => composeEmail(sub)}>
                Compose Email
              </button>
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            {sub.archived ? (
              <button className="editor-btn" onClick={() => unarchiveSub(sub.id)}>
                <Icons.Restore /> Restore Submission
              </button>
            ) : (
              <button className="editor-btn" onClick={() => setDeleteConfirm({
                title: "Archive Submission",
                body: "This submission will move to the Archived tab. It stays viewable and can be restored at any time.",
                confirmLabel: "Archive",
                action: () => archiveSub(sub.id),
              })}>
                <Icons.Archive /> Archive Submission
              </button>
            )}
          </div>
        </div>
        )}
      </div>
    );
  }

  // ─── Dashboard ───
  return (
    <div className="admin-dash">
      <DeleteModal />
      <h1 style={{ fontFamily: "var(--serif)", fontSize: "2rem", fontWeight: 400, marginBottom: 8, color: "#000" }}>Admin Dashboard</h1>
      <p style={{ color: "var(--text-light)", marginBottom: 32, fontSize: "0.9rem" }}>
        <Icons.Lock /> HIPAA-compliant secure dashboard — session expires in: {countdown}{session?.ipAddress ? ` — IP: ${session.ipAddress}` : ""}
      </p>

      <div className="admin-tabs" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0 }}>
        <button className={`admin-tab ${tab === "submissions" ? "active" : ""}`} onClick={() => setTab("submissions")}>
          Submissions ({subs.length})
        </button>
        <button className={`admin-tab ${tab === "archived" ? "active" : ""}`} onClick={() => setTab("archived")}>
          Archived ({archivedSubs.length})
        </button>
        <button className={`admin-tab ${tab === "blog" ? "active" : ""}`} onClick={() => setTab("blog")}>
          Blog ({blogs.length})
        </button>
        <button className={`admin-tab ${tab === "links" ? "active" : ""}`} onClick={() => setTab("links")}>
          External Links ({links.length})
        </button>
        {admin?.role === "MASTER_ADMIN" && (
          <button className={`admin-tab ${tab === "admins" ? "active" : ""}`} onClick={() => setTab("admins")}>
            Admin Users ({admins.length})
          </button>
        )}
        {admin?.role === "MASTER_ADMIN" && (
          <button className={`admin-tab ${tab === "audit" ? "active" : ""}`} onClick={() => setTab("audit")}>
            Audit Log
          </button>
        )}
        <button className={`admin-tab ${tab === "profile" ? "active" : ""}`} onClick={() => setTab("profile")}>
          Profile
        </button>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12, padding: "4px 0" }}>
          <span style={{ fontSize: "0.85rem", color: "var(--text-light)", fontWeight: 500 }}>{admin?.username}</span>
          <button className="btn-outline" style={{ padding: "6px 14px", fontSize: "0.82rem" }} onClick={logout}>Log Out</button>
        </div>
      </div>

      {/* ─── SUBMISSIONS TAB ─── */}
      {tab === "submissions" && (
        <div>
          {!subsLoading && subs.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <button className="btn-outline" onClick={exportCsv} style={{ fontSize: "0.85rem" }}>
                Export to CSV
              </button>
            </div>
          )}
          {subsLoading ? (
            <div className="admin-card text-center" style={{ padding: 60 }}>
              <p style={{ color: "var(--text-light)" }}>Loading submissions...</p>
            </div>
          ) : subs.length === 0 ? (
            <div className="admin-card text-center" style={{ padding: 60 }}>
              <p style={{ color: "var(--text-light)" }}>No form submissions yet.</p>
            </div>
          ) : subs.map(s => (
            <div key={s.id} className="admin-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div onClick={() => openSubmissionInNewTab(s.id)} style={{ cursor: "pointer", flex: 1 }}>
                <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.1rem", color: "#000" }}>{s.firstName} {s.lastName}</h3>
                <p style={{ fontSize: "0.85rem", color: "var(--text-light)" }}>{s.email} • {s.date}</p>
                <p style={{ fontSize: "0.88rem", color: "#000", marginTop: 4 }}>{s.concern?.substring(0, 80)}{s.concern?.length > 80 ? "..." : ""}</p>
              </div>
              <div style={{ display: "flex", gap: 8, marginLeft: 16 }}>
                <button className="editor-btn" title="View" onClick={() => openSubmissionInNewTab(s.id)}><Icons.Eye /></button>
                <button className="editor-btn" title="Archive" onClick={() => setDeleteConfirm({
                  title: "Archive Submission",
                  body: `Archive the submission from ${s.firstName} ${s.lastName}? It moves to the Archived tab and can be restored at any time.`,
                  confirmLabel: "Archive",
                  action: () => archiveSub(s.id),
                })}><Icons.Archive /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── ARCHIVED SUBMISSIONS TAB ─── */}
      {tab === "archived" && (
        <div>
          <p style={{ fontSize: "0.85rem", color: "var(--text-light)", marginBottom: 16 }}>
            Archived submissions are retained, not deleted. Restore one to move it back to the Submissions tab.
          </p>
          {subsLoading ? (
            <div className="admin-card text-center" style={{ padding: 60 }}>
              <p style={{ color: "var(--text-light)" }}>Loading archived submissions...</p>
            </div>
          ) : archivedSubs.length === 0 ? (
            <div className="admin-card text-center" style={{ padding: 60 }}>
              <p style={{ color: "var(--text-light)" }}>No archived submissions.</p>
            </div>
          ) : archivedSubs.map(s => (
            <div key={s.id} className="admin-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div onClick={() => openSubmissionInNewTab(s.id)} style={{ cursor: "pointer", flex: 1 }}>
                <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.1rem", color: "#000" }}>{s.firstName} {s.lastName}</h3>
                <p style={{ fontSize: "0.85rem", color: "var(--text-light)" }}>{s.email} • {s.date}</p>
                <p style={{ fontSize: "0.88rem", color: "#000", marginTop: 4 }}>{s.concern?.substring(0, 80)}{s.concern?.length > 80 ? "..." : ""}</p>
              </div>
              <div style={{ display: "flex", gap: 8, marginLeft: 16 }}>
                <button className="editor-btn" title="View" onClick={() => openSubmissionInNewTab(s.id)}><Icons.Eye /></button>
                <button className="editor-btn" title="Restore" onClick={() => unarchiveSub(s.id)}><Icons.Restore /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── BLOG TAB ─── */}
      {tab === "blog" && (
        <div>
          {!editing && (
            <div style={{ marginBottom: 24 }}>
              <button className="btn-primary" onClick={startNewBlog}>+ New Blog Post</button>
            </div>
          )}

          {editing && (
            <div className="admin-card" style={{ marginBottom: 24 }}>
              <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.2rem", marginBottom: 20, color: "#000" }}>
                {editing === "new" ? "New Post" : "Edit Post"}
              </h3>
              <div className="form-group">
                <label>Title</label>
                <input className="form-input" value={blogForm.title} onChange={e => setBlogForm({...blogForm, title: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input className="form-input" value={blogForm.desc} onChange={e => setBlogForm({...blogForm, desc: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Cover Image</label>
                <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                  <button className="editor-btn" onClick={() => setBlogForm({ ...blogForm, imgSource: "upload" })}>Upload</button>
                  <button className="editor-btn" onClick={() => setBlogForm({ ...blogForm, imgSource: "url" })}>Image URL</button>
                </div>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  {blogForm.imgSource === "upload" ? (
                    <input type="file" accept="image/*" onChange={handleImgUpload} style={{ fontSize: "0.88rem" }} />
                  ) : (
                    <input
                      className="form-input"
                      placeholder="https://..."
                      value={blogForm.imgUrl}
                      onChange={e => setBlogForm({ ...blogForm, imgUrl: e.target.value })}
                    />
                  )}
                  {(blogForm.imgUrl || blogForm.imgAssetId) && (
                    <button className="editor-btn" style={{ color: "#c00" }} onClick={() => setBlogForm({ ...blogForm, imgUrl: "", imgAssetId: "" })}>
                      <Icons.Trash /> Remove
                    </button>
                  )}
                </div>
                {(blogForm.imgUrl || blogForm.imgAssetId) && (
                  <img src={blogForm.imgSource === "url" ? blogForm.imgUrl : getBlogImageUrlFromAsset(blogForm.imgAssetId)} alt="Preview" style={{ marginTop: 12, maxWidth: 240, borderRadius: 8 }} />
                )}
              </div>
              <div className="form-group">
                <label>Link Media (YouTube URL)</label>
                <input className="form-input" value={blogForm.media} onChange={e => setBlogForm({...blogForm, media: e.target.value})} placeholder="https://youtube.com/watch?v=..." />
              </div>
              <div className="form-group" style={{ position: "relative" }}>
                <label>Content</label>
                <div className="editor-bar">
                  <button className="editor-btn" onClick={() => applyFormat("bold")}><b>B</b></button>
                  <button className="editor-btn" onClick={() => applyFormat("italic")}><i>I</i></button>
                  <button className="editor-btn" onClick={() => applyFormat("formatBlock", "H2")}>H2</button>
                  <button className="editor-btn" onClick={() => applyFormat("formatBlock", "H3")}>H3</button>
                  <button className="editor-btn" onClick={() => applyFormat("insertUnorderedList")}>List</button>
                  <button className="editor-btn" onClick={() => applyFormat("insertOrderedList")}>1.</button>
                  <button className="editor-btn" onClick={() => applyFormat("formatBlock", "BLOCKQUOTE")}>"</button>
                  <button className="editor-btn" onClick={insertLink}><Icons.Link /></button>
                </div>
                <div
                  ref={editorRef}
                  className="rich-editor"
                  dir="ltr"
                  contentEditable
                  suppressContentEditableWarning
                  onInput={(e) => { contentHtmlRef.current = e.currentTarget.innerHTML; }}
                />
                {linkPopover && (
                  <div className="link-popover" style={{ top: linkPopover.top, left: linkPopover.left }}>
                    <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "#555" }}>Display text</label>
                    <input
                      value={linkPopover.text}
                      onChange={e => setLinkPopover(p => ({ ...p, text: e.target.value }))}
                      placeholder="Link text"
                      autoFocus
                    />
                    <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "#555", marginTop: 2 }}>URL</label>
                    <input
                      value={linkPopover.url}
                      onChange={e => setLinkPopover(p => ({ ...p, url: e.target.value }))}
                      onKeyDown={e => { if (e.key === "Enter") applyLinkPopover(); }}
                      placeholder="https://example.com"
                    />
                    <div className="link-popover-actions">
                      <button style={{ background: "var(--olive)", color: "#fff", border: "none" }} onClick={applyLinkPopover}>Apply</button>
                      <button style={{ background: "#fff" }} onClick={cancelLinkPopover}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <button className="btn-primary" onClick={saveBlog}>Save Post</button>
                <button className="btn-outline" onClick={() => setEditing(null)}>Cancel</button>
              </div>
            </div>
          )}

          {!editing && blogs.map(b => (
            <div key={b.id} className="admin-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.05rem", color: "#000" }}>{b.title}</h3>
                <p style={{ fontSize: "0.82rem", color: "var(--text-light)" }}>{b.date}</p>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="editor-btn" onClick={() => startEditBlog(b)}><Icons.Pen /> Edit</button>
                <button className="editor-btn" style={{ color: "#c00" }} onClick={() => setDeleteConfirm({ action: () => deleteBlog(b.id) })}><Icons.Trash /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── LINKS TAB ─── */}
      {tab === "links" && (
        <div>
          {!editingLink && (
            <div style={{ marginBottom: 24 }}>
              <button className="btn-primary" onClick={() => { setLinkForm({ title: "", url: "", desc: "" }); setEditingLink("new"); }}>
                + New External Link
              </button>
            </div>
          )}

          {editingLink && (
            <div className="admin-card" style={{ marginBottom: 24 }}>
              <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.2rem", marginBottom: 20, color: "#000" }}>
                {editingLink === "new" ? "New Link" : "Edit Link"}
              </h3>
              <div className="form-group">
                <label>Title</label>
                <input className="form-input" value={linkForm.title} onChange={e => setLinkForm({...linkForm, title: e.target.value})} />
              </div>
              <div className="form-group">
                <label>URL</label>
                <input className="form-input" value={linkForm.url} onChange={e => setLinkForm({...linkForm, url: e.target.value})} placeholder="https://..." />
              </div>
              <div className="form-group">
                <label>Description (optional)</label>
                <input className="form-input" value={linkForm.desc} onChange={e => setLinkForm({...linkForm, desc: e.target.value})} />
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <button className="btn-primary" onClick={saveLink}>Save Link</button>
                <button className="btn-outline" onClick={() => setEditingLink(null)}>Cancel</button>
              </div>
            </div>
          )}

          {!editingLink && links.length === 0 && (
            <div className="admin-card text-center" style={{ padding: 60 }}>
              <p style={{ color: "var(--text-light)" }}>No external links yet. Add your first one above.</p>
            </div>
          )}

          {!editingLink && links.map(l => (
            <div key={l.id} className="admin-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: "0.95rem", fontWeight: 600, color: "#000" }}>{l.title}</h3>
                <p style={{ fontSize: "0.82rem", color: "var(--olive)" }}>{l.url}</p>
                {l.desc && <p style={{ fontSize: "0.82rem", color: "var(--text-light)" }}>{l.desc}</p>}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="editor-btn" onClick={() => { setLinkForm({ title: l.title, url: l.url, desc: l.desc || "" }); setEditingLink(l.id); }}>
                  <Icons.Pen />
                </button>
                <button className="editor-btn" style={{ color: "#c00" }} onClick={() => setDeleteConfirm({ action: () => deleteLink(l.id) })}>
                  <Icons.Trash />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── ADMINS TAB (MASTER ONLY) ─── */}
      {tab === "admins" && admin?.role === "MASTER_ADMIN" && (
        <div>
          {!editingAdminId && (
            <div style={{ marginBottom: 24 }}>
              <button className="btn-primary" onClick={() => {
                setAdminForm({
                  username: "",
                  email: "",
                  password: "",
                  role: "USER_ADMIN",
                  receivesEmails: true,
                  authCodeRequired: true,
                  isActive: true,
                });
                setAdminFormMsg("");
                setEditingAdminId("new");
              }}>
                + New Admin User
              </button>
            </div>
          )}

          {editingAdminId && (
            <div className="admin-card" style={{ marginBottom: 24 }}>
              <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.2rem", marginBottom: 20, color: "#000" }}>
                {editingAdminId === "new" ? "Create Admin User" : "Edit Admin User"}
              </h3>
              <div className="form-group">
                <label>Username</label>
                <input className="form-input" value={adminForm.username} onChange={e => setAdminForm({ ...adminForm, username: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input className="form-input" value={adminForm.email} onChange={e => setAdminForm({ ...adminForm, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label>{editingAdminId === "new" ? "Password" : "Password (leave blank to keep current)"}</label>
                <input className="form-input" type="password" value={adminForm.password} onChange={e => setAdminForm({ ...adminForm, password: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select className="form-input" value={adminForm.role} onChange={e => setAdminForm({ ...adminForm, role: e.target.value })}>
                  <option value="USER_ADMIN">User Admin</option>
                  <option value="MASTER_ADMIN">Master Admin</option>
                </select>
              </div>
              <div className="checkbox-row">
                <label>Receives email notifications</label>
                <div className={`custom-checkbox ${adminForm.receivesEmails ? "checked" : ""}`} onClick={() => setAdminForm({ ...adminForm, receivesEmails: !adminForm.receivesEmails })} />
              </div>
              <div className="checkbox-row">
                <label>Authentication code required (login and recovery)</label>
                <div className={`custom-checkbox ${adminForm.authCodeRequired ? "checked" : ""}`} onClick={() => setAdminForm({ ...adminForm, authCodeRequired: !adminForm.authCodeRequired })} />
              </div>
              <div className="checkbox-row" style={{ opacity: editingAdminId === admin?.id ? 0.5 : 1 }}>
                <label>Account active{editingAdminId === admin?.id ? " (cannot deactivate own account)" : ""}</label>
                <div className={`custom-checkbox ${adminForm.isActive ? "checked" : ""}`} onClick={() => { if (editingAdminId !== admin?.id) setAdminForm({ ...adminForm, isActive: !adminForm.isActive }); }} />
              </div>
              {adminFormMsg && (
                <p style={{ color: "#c00", fontSize: "0.88rem", marginBottom: 12 }}>{adminFormMsg}</p>
              )}
              <div style={{ display: "flex", gap: 12 }}>
                <button className="btn-primary" onClick={saveAdminUser}>Save Admin User</button>
                <button className="btn-outline" onClick={() => { setEditingAdminId(null); setAdminFormMsg(""); }}>Cancel</button>
              </div>
            </div>
          )}

          {!editingAdminId && admins.map(a => (
            <div key={a.id} className="admin-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: "1rem", fontWeight: 600, color: "#000" }}>{a.username} ({a.role === "MASTER_ADMIN" ? "Master Admin" : "User Admin"})</h3>
                <p style={{ fontSize: "0.85rem", color: "var(--text-light)" }}>{a.email}</p>
                <p style={{ fontSize: "0.82rem", color: "var(--text-light)" }}>
                  Status: {a.isActive ? "Active" : "Disabled"} | Receives emails: {a.receivesEmails ? "Yes" : "No"} | Auth code: {a.authCodeRequired ? "Required" : "Disabled"}
                </p>
                {a.failedLoginCount > 0 && (
                  <p style={{ fontSize: "0.82rem", color: "var(--text-light)" }}>
                    {a.failedLoginCount} failed login attempt{a.failedLoginCount === 1 ? "" : "s"} since the last success
                  </p>
                )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="editor-btn" onClick={() => {
                  setAdminForm({
                    username: a.username,
                    email: a.email,
                    password: "",
                    role: a.role,
                    receivesEmails: !!a.receivesEmails,
                    authCodeRequired: !!a.authCodeRequired,
                    isActive: !!a.isActive,
                  });
                  setAdminFormMsg("");
                  setEditingAdminId(a.id);
                }}>
                  <Icons.Pen />
                </button>
                {a.id !== admin?.id && (
                  <button className="editor-btn" style={{ color: "#c00" }} onClick={() => setDeleteConfirm({ action: () => deleteAdminUser(a.id) })}>
                    <Icons.Trash />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── AUDIT LOG TAB (MASTER ONLY) ─── */}
      {tab === "audit" && admin?.role === "MASTER_ADMIN" && (
        <div>
          <p style={{ fontSize: "0.85rem", color: "var(--text-light)", marginBottom: 16 }}>{auditTotal} total entries</p>
          {auditLoading ? (
            <div className="admin-card text-center" style={{ padding: 60 }}>
              <p style={{ color: "var(--text-light)" }}>Loading...</p>
            </div>
          ) : auditLogs.length === 0 ? (
            <div className="admin-card text-center" style={{ padding: 60 }}>
              <p style={{ color: "var(--text-light)" }}>No audit log entries yet.</p>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #eee", textAlign: "left" }}>
                    <th style={{ padding: "8px 12px", color: "var(--text-light)", fontWeight: 600 }}>Time</th>
                    <th style={{ padding: "8px 12px", color: "var(--text-light)", fontWeight: 600 }}>Actor</th>
                    <th style={{ padding: "8px 12px", color: "var(--text-light)", fontWeight: 600 }}>Action</th>
                    <th style={{ padding: "8px 12px", color: "var(--text-light)", fontWeight: 600 }}>Entity</th>
                    <th style={{ padding: "8px 12px", color: "var(--text-light)", fontWeight: 600 }}>Details</th>
                    <th style={{ padding: "8px 12px", color: "var(--text-light)", fontWeight: 600 }}>IP Address</th>
                    <th style={{ padding: "8px 12px", color: "var(--text-light)", fontWeight: 600 }}>IP Hash</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map(l => (
                    <tr key={l.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>{formatAuditDate(l.createdAt)}</td>
                      <td style={{ padding: "8px 12px", fontWeight: 500 }}>{l.actorUsername}</td>
                      <td style={{ padding: "8px 12px" }}>{l.action}</td>
                      <td style={{ padding: "8px 12px", color: "var(--text-light)" }}>
                        {l.entityType}
                        {l.entityId && (
                          <>
                            {" "}
                            <span style={{ fontFamily: "monospace", fontSize: "0.78rem" }} title={l.entityId}>{l.entityId}</span>
                          </>
                        )}
                      </td>
                      <td style={{ padding: "8px 12px", color: "var(--text-light)", maxWidth: 280, overflowWrap: "anywhere" }}>
                        {formatAuditMeta(l.metadata) || "—"}
                      </td>
                      <td style={{ padding: "8px 12px", fontFamily: "monospace", fontSize: "0.82rem" }}>{l.ipAddress || "—"}</td>
                      <td
                        style={{ padding: "8px 12px", fontFamily: "monospace", fontSize: "0.78rem", color: copiedHashId === l.id ? "var(--olive)" : "var(--text-light)", cursor: l.ipHash ? "copy" : "default", userSelect: "none", whiteSpace: "nowrap" }}
                        onClick={() => copyIpHash(l.id, l.ipHash)}
                        title={l.ipHash ? "Click to copy full hash" : undefined}
                      >
                        {l.ipHash ? (copiedHashId === l.id ? "Copied ✓" : l.ipHash.slice(0, 12) + "...") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {auditPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 20 }}>
              <button className="btn-outline" disabled={auditPage <= 1} onClick={() => setAuditPage(p => p - 1)} style={{ fontSize: "0.85rem" }}>Previous</button>
              <span style={{ fontSize: "0.85rem", color: "var(--text-light)", alignSelf: "center" }}>Page {auditPage} of {auditPages}</span>
              <button className="btn-outline" disabled={auditPage >= auditPages} onClick={() => setAuditPage(p => p + 1)} style={{ fontSize: "0.85rem" }}>Next</button>
            </div>
          )}
        </div>
      )}

      {/* ─── PROFILE TAB ─── */}
      {tab === "profile" && (
        <div className="admin-card">
          <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.2rem", marginBottom: 12, color: "#000" }}>My Profile</h3>
          <p style={{ color: "var(--text-light)", marginBottom: 14 }}>Role: {admin?.role === "MASTER_ADMIN" ? "Master Admin" : "User Admin"}</p>
          <div className="form-group">
            <label>Email</label>
            <input className="form-input" value={myEmail} onChange={e => setMyEmail(e.target.value)} />
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <button className="btn-primary" onClick={saveMyEmail}>Update Email</button>
          </div>
          {myEmailMsg && <p style={{ marginTop: 10, color: myEmailMsg.toLowerCase().includes("unable") ? "#c00" : "var(--olive)" }}>{myEmailMsg}</p>}
        </div>
      )}
    </div>
  );
}


function NotFoundPage() {
  const { go } = useRoute();
  useSeo({ title: "Page Not Found", description: "The page you are looking for could not be found.", path: "/404", noindex: true });
  return (
    <div>
      <section className="page-hero" style={{ padding: "140px 24px 60px" }}>
        <p className="section-label">404</p>
        <h1>Page Not Found</h1>
        <p>Sorry, the page you’re looking for doesn’t exist or has moved.</p>
        <div style={{ marginTop: 24 }}>
          <button className="btn-primary" onClick={() => go("/")}>Return Home <Icons.Arrow /></button>
        </div>
      </section>
    </div>
  );
}

// ─── MAIN APP ───────────────────────────────────────────────
function normalizePath(p) {
  const cleaned = String(p || "/").replace(/\/+$/, "");
  return cleaned === "" ? "/" : cleaned;
}

export default function App() {
  const [page, setPage] = useState(normalizePath(window.location.pathname));
  const [loggedIn, setLoggedIn] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [session, setSession] = useState(null);

  const scrollToHash = useCallback((id, delay = 350) => {
    if (!id) return;
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, delay);
  }, []);

  const go = useCallback((path) => {
    const target = String(path || "/");
    const [pathPart, hashPart] = target.split("#");
    const cleanPath = normalizePath(pathPart);
    window.history.pushState({}, "", cleanPath + (hashPart ? `#${hashPart}` : ""));
    setPage(cleanPath);
    if (hashPart) scrollToHash(hashPart);
    else window.scrollTo({ top: 0, behavior: "instant" });
  }, [scrollToHash]);

  // Crawlable anchor props: real href for Googlebot, SPA navigation on click,
  // while preserving modifier-click / middle-click to open in a new tab.
  const linkProps = useCallback((path) => ({
    href: path,
    onClick: (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      go(path);
    },
  }), [go]);

  useEffect(() => {
    const handler = () => {
      setPage(normalizePath(window.location.pathname));
      window.scrollTo({ top: 0, behavior: "instant" });
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  // Honor in-page anchors on first load (e.g. /contact#faq).
  useEffect(() => {
    if (window.location.hash) scrollToHash(window.location.hash.slice(1), 450);
  }, [scrollToHash]);

  const refreshSession = useCallback(async () => {
    try {
      const resp = await api.authMe();
      setLoggedIn(true);
      setAdmin(resp.admin || null);
      setSession(resp.session || null);
      return resp.admin || null;
    } catch {
      setLoggedIn(false);
      setAdmin(null);
      setSession(null);
      return null;
    }
  }, []);

  // Only validate the admin session on admin routes. Public visitors never hit
  // the auth endpoint (faster, cheaper, and no 401 logged on marketing pages).
  useEffect(() => {
    if (page.startsWith("/admin")) refreshSession();
  }, [page, refreshSession]);

  const login = (nextAdmin = null) => {
    setLoggedIn(true);
    setAdmin(nextAdmin);
  };
  const logout = async () => {
    try {
      await api.authLogout();
    } catch {}
    setLoggedIn(false);
    setAdmin(null);
    setSession(null);
    go("/");
  };

  const renderPage = () => {
    if (page === "/" || page === "") return <HomePage />;
    if (page === "/about") return <AboutPage />;
    if (page.startsWith("/services")) return <ServicesPage />;
    if (page === "/child-psychology") return <ChildPsychologyPage />;
    if (page === "/testimonials") return <TestimonialsPage />;
    if (page === "/blog") return <BlogPage />;
    if (page.startsWith("/blog/")) return <BlogPostPage slug={page.split("/blog/")[1]} />;
    if (page === "/contact") return <ContactPage />;
    if (page.startsWith("/admin")) return <AdminPage />;
    return <NotFoundPage />;
  };

  return (
    <AuthCtx.Provider value={{ loggedIn, admin, session, login, logout, refreshSession }}>
      <RouteCtx.Provider value={{ page, go, linkProps }}>
        <style>{css}</style>
        <Navbar />
        <main style={{ paddingTop: page === "/" ? 0 : undefined }}>
          {renderPage()}
        </main>
        {!page.startsWith("/admin") || !loggedIn ? <Footer /> : null}
      </RouteCtx.Provider>
    </AuthCtx.Provider>
  );
}